<template>
  <div class="entries-collectors-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-handshake"></i>
        Fechamento
      </h1>
      <p class="page-subtitle">Feche o Líquido Prêmio (LP) banca → cobrador e acompanhe os pagamentos</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button
          type="button"
          class="tab-button"
          :class="{ active: selectedTab === 1 }"
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-search"></i>
          CONSULTA / FECHAR
        </button>
        <button
          type="button"
          class="tab-button"
          :class="{ active: selectedTab === 2 }"
          @click="switchToHistorico"
        >
          <i class="fa-solid fa-clock-rotate-left"></i>
          FECHAMENTOS
        </button>
      </nav>
    </div>

    <!-- ========== CONSULTA ========== -->
    <div v-show="selectedTab === 1" class="tab-content">
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros de Consulta
          </h3>
        </div>
        <div class="filters-content">
          <div class="filters-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-check"></i>
                Cobradores *
              </label>
              <div class="multiselect-container">
                <MultiSelect
                  v-model="selectedCollectorsObjects"
                  :options="availableCollectors"
                  placeholder="Selecione os cobradores"
                  :showSelectAllButtons="true"
                />
              </div>
              <small v-if="currentSelectedRegion" class="filter-hint">
                Exibindo cobradores da região <strong>{{ currentSelectedRegion.name }}</strong>
              </small>
              <small v-else-if="currentSelectedBanca" class="filter-hint">
                Exibindo cobradores da banca <strong>{{ currentSelectedBanca.name }}</strong>
              </small>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-store"></i>
                Vendedores
              </label>
              <div class="multiselect-container">
                <MultiSelect
                  v-model="selectedSellersObjects"
                  :options="availableSellers"
                  placeholder="Todos os vendedores (ou selecione)"
                  :showSelectAllButtons="true"
                  :disabled="!selectedCollectorsObjects.length"
                />
              </div>
              <small v-if="!selectedCollectorsObjects.length" class="filter-hint">
                Selecione cobrador(es) para listar os vendedores
              </small>
            </div>

            <div class="input-group" v-if="showManualPeriodDates">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Início *
              </label>
              <input type="date" v-model="periodStart" class="form-input" />
            </div>

            <div v-if="showManualPeriodDates" class="input-group period-auto-info">
              <label class="input-label">
                <i class="fa-solid fa-clock"></i>
                Data/hora fim
              </label>
              <div class="auto-period-box">
                Até o momento em que você clicar em
                <strong>Gerar fechamento</strong>
                (hora atual). O próximo fechamento começa daí em diante.
              </div>
            </div>

            <div v-else class="input-group period-auto-info">
              <label class="input-label">
                <i class="fa-solid fa-calendar-check"></i>
                Período automático
              </label>
              <div class="auto-period-box">
                Do último fechamento até o momento em que você clicar em
                <strong>Gerar fechamento</strong>:
                <strong>{{ autoPeriodRangeLabel }}</strong>
              </div>
            </div>
          </div>

          <div class="filters-actions">
            <button
              type="button"
              class="btn-primary"
              @click="generateSettlements"
              :disabled="loadingPreview || !!savingSettlementId"
            >
              <i
                v-if="loadingPreview || savingSettlementId"
                class="fa-solid fa-spinner fa-spin"
              ></i>
              <i v-else class="fa-solid fa-file-invoice-dollar"></i>
              {{
                loadingPreview || savingSettlementId
                  ? "Carregando..."
                  : "Gerar fechamento"
              }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="previewTotals" class="summary-row">
        <div class="summary-card">
          <div class="summary-label">Líquido Prêmio (período)</div>
          <div
            class="summary-value"
            :class="previewTotals.liquidPrizePeriod >= 0 ? 'positive' : 'negative'"
          >
            {{ setCurrencyString(previewTotals.liquidPrizePeriod) }}
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Saldo anterior em aberto</div>
          <div class="summary-value warning">
            {{ setCurrencyString(previewTotals.previousOpen) }}
          </div>
        </div>
        <div class="summary-card highlight">
          <div class="summary-label">Total a pagar (cobrador → banca)</div>
          <div class="summary-value positive">
            {{ setCurrencyString(previewTotals.totalDue) }}
          </div>
        </div>
      </div>

      <div v-if="previews.length" class="results-card results-toolbar-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-table"></i>
            Resultado da consulta
          </h3>
          <div class="card-actions">
            <div class="table-zoom-controls" title="Zoom da tabela">
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom <= 5"
                @click="decreaseTableZoom"
                title="Diminuir zoom"
              >
                <i class="fa-solid fa-magnifying-glass-minus"></i>
              </button>
              <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
                {{ tableZoom }}%
              </span>
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom >= 100"
                @click="increaseTableZoom"
                title="Aumentar zoom"
              >
                <i class="fa-solid fa-magnifying-glass-plus"></i>
              </button>
            </div>
            <div class="columns-dropdown" ref="previewColumnsDropdown">
              <button
                type="button"
                class="btn-action btn-columns"
                @click.stop="showPreviewColumnsMenu = !showPreviewColumnsMenu"
                title="Mostrar ou esconder colunas"
              >
                <i class="fa-solid fa-columns"></i>
                Colunas
                <i class="fa-solid fa-caret-down columns-caret"></i>
              </button>
              <div v-if="showPreviewColumnsMenu" class="columns-menu" @click.stop>
                <div class="columns-menu-header">
                  <span>Colunas visíveis</span>
                  <button type="button" class="columns-reset" @click="resetPreviewVisibleColumns">
                    Restaurar
                  </button>
                </div>
                <label
                  v-for="col in previewToggleableColumns"
                  :key="col.key"
                  class="columns-menu-item"
                  :class="{ locked: col.locked }"
                >
                  <input
                    type="checkbox"
                    :checked="isPreviewColumnVisible(col.key)"
                    :disabled="col.locked"
                    @change="togglePreviewColumn(col.key)"
                  />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>
            <button
              type="button"
              class="btn-action btn-print"
              @click="openPdfModal('preview', 'print')"
              title="Imprimir relatório"
            >
              <i class="fa-solid fa-print"></i>
              Imprimir
            </button>
            <button
              type="button"
              class="btn-action btn-pdf"
              @click="openPdfModal('preview', 'pdf')"
              title="Exportar para PDF"
            >
              <i class="fa-solid fa-file-pdf"></i>
              PDF
            </button>
            <button
              type="button"
              class="btn-action btn-excel"
              @click="exportPreviewToExcel"
              title="Exportar para Excel"
            >
              <i class="fa-solid fa-file-excel"></i>
              Excel
            </button>
          </div>
        </div>
      </div>

      <div
        v-for="preview in previews"
        :key="preview.collectorId"
        class="results-card"
      >
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-users"></i>
            Vendedores de {{ preview.collectorName }}
            <span v-if="preview.regionName" class="muted">
              — {{ preview.regionName }}
            </span>
          </h3>
          <button
            type="button"
            class="btn-success"
            @click="createSettlement(preview)"
            :disabled="savingSettlementId === preview.collectorId"
          >
            <i
              :class="
                savingSettlementId === preview.collectorId
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-file-invoice-dollar'
              "
            ></i>
            Efetuar Fechamento
          </button>
        </div>

        <div class="mini-summary">
          <span>LP: <strong>{{ setCurrencyString(preview.liquidPrizePeriod) }}</strong></span>
          <span>Anterior: <strong>{{ setCurrencyString(preview.previousOpen) }}</strong></span>
          <span>A pagar: <strong class="currency-positive">{{ setCurrencyString(preview.totalDue) }}</strong></span>
        </div>

        <div class="table-container" :style="tableZoomStyle">
          <table class="modern-table">
            <thead>
              <tr>
                <th v-if="isPreviewColumnVisible('sellerName')">Vendedor</th>
                <th v-if="isPreviewColumnVisible('previousBalance')">Saldo anterior</th>
                <th v-if="isPreviewColumnVisible('sales')">Vendas</th>
                <th v-if="isPreviewColumnVisible('commission')">Comissão</th>
                <th v-if="isPreviewColumnVisible('liquid')">Líquido</th>
                <th v-if="isPreviewColumnVisible('prizes')">Prêmios</th>
                <th v-if="isPreviewColumnVisible('liquidPrize')">LP</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in preview.sellers"
                :key="s.sellerId"
                class="data-row"
              >
                <td v-if="isPreviewColumnVisible('sellerName')" class="name-column">
                  {{ s.sellerName }}
                </td>
                <td v-if="isPreviewColumnVisible('previousBalance')">
                  {{ setCurrencyString(s.previousBalance) }}
                </td>
                <td v-if="isPreviewColumnVisible('sales')">
                  <span
                    v-if="Number(s.sales || 0) > 0"
                    class="currency-value currency-positive clickable-value"
                    title="Clique para ver as vendas"
                    @click="openSalesModal(s, preview)"
                  >
                    {{ setCurrencyString(s.sales) }}
                  </span>
                  <span v-else class="currency-value currency-positive">
                    {{ setCurrencyString(s.sales) }}
                  </span>
                </td>
                <td v-if="isPreviewColumnVisible('commission')">
                  {{ setCurrencyString(s.commission) }}
                </td>
                <td v-if="isPreviewColumnVisible('liquid')">
                  {{ setCurrencyString(s.liquid) }}
                </td>
                <td v-if="isPreviewColumnVisible('prizes')">
                  {{ setCurrencyString(s.prizes) }}
                </td>
                <td
                  v-if="isPreviewColumnVisible('liquidPrize')"
                  :class="{
                    'currency-positive': s.liquidPrize > 0,
                    'currency-negative': s.liquidPrize < 0,
                  }"
                >
                  <strong>{{ setCurrencyString(s.liquidPrize) }}</strong>
                </td>
              </tr>
              <tr v-if="!(preview.sellers || []).length">
                <td :colspan="previewVisibleColumnsCount" class="empty">
                  Nenhum vendedor vinculado neste período
                </td>
              </tr>
            </tbody>
            <tfoot v-if="(preview.sellers || []).length">
              <tr>
                <td v-if="isPreviewColumnVisible('sellerName')" class="name-column">
                  <strong>Total:</strong>
                </td>
                <td v-if="isPreviewColumnVisible('previousBalance')">
                  <strong>{{ setCurrencyString(sumPreviewField(preview, "previousBalance")) }}</strong>
                </td>
                <td v-if="isPreviewColumnVisible('sales')">
                  <strong>{{ setCurrencyString(sumPreviewField(preview, "sales")) }}</strong>
                </td>
                <td v-if="isPreviewColumnVisible('commission')">
                  <strong>{{ setCurrencyString(sumPreviewField(preview, "commission")) }}</strong>
                </td>
                <td v-if="isPreviewColumnVisible('liquid')">
                  <strong>{{ setCurrencyString(sumPreviewField(preview, "liquid")) }}</strong>
                </td>
                <td v-if="isPreviewColumnVisible('prizes')">
                  <strong>{{ setCurrencyString(sumPreviewField(preview, "prizes")) }}</strong>
                </td>
                <td
                  v-if="isPreviewColumnVisible('liquidPrize')"
                  :class="{
                    'currency-positive': sumPreviewField(preview, 'liquidPrize') > 0,
                    'currency-negative': sumPreviewField(preview, 'liquidPrize') < 0,
                  }"
                >
                  <strong>{{ setCurrencyString(sumPreviewField(preview, "liquidPrize")) }}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- ========== HISTÓRICO ========== -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros
          </h3>
        </div>
        <div class="filters-content">
          <div class="filters-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-check"></i>
                Cobradores
              </label>
              <div class="multiselect-container">
                <MultiSelect
                  v-model="historyCollectorsObjects"
                  :options="availableCollectors"
                  placeholder="Todos os cobradores (ou selecione)"
                  :showSelectAllButtons="true"
                />
              </div>
              <small v-if="currentSelectedRegion" class="filter-hint">
                Exibindo cobradores da região <strong>{{ currentSelectedRegion.name }}</strong>
              </small>
              <small v-else-if="currentSelectedBanca" class="filter-hint">
                Exibindo cobradores da banca <strong>{{ currentSelectedBanca.name }}</strong>
              </small>
            </div>
            <div class="input-group">
              <label class="input-label">Status</label>
              <select v-model="historyStatus" class="form-input">
                <option value="">Todos</option>
                <option value="OPEN">Em aberto</option>
                <option value="PARTIAL">Parcial</option>
                <option value="PAID">Quitado</option>
              </select>
            </div>
          </div>
          <div class="filters-actions">
            <button
              type="button"
              class="btn-primary"
              @click="loadSettlements"
              :disabled="loadingHistory"
            >
              <i v-if="loadingHistory" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-search"></i>
              {{ loadingHistory ? "Consultando..." : "Pesquisar" }}
            </button>
          </div>
        </div>
      </div>

      <div class="results-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-list"></i>
            Histórico de fechamentos
          </h3>
          <div class="card-actions">
            <div class="table-zoom-controls" title="Zoom da tabela">
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom <= 5"
                @click="decreaseTableZoom"
                title="Diminuir zoom"
              >
                <i class="fa-solid fa-magnifying-glass-minus"></i>
              </button>
              <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
                {{ tableZoom }}%
              </span>
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom >= 100"
                @click="increaseTableZoom"
                title="Aumentar zoom"
              >
                <i class="fa-solid fa-magnifying-glass-plus"></i>
              </button>
            </div>
            <div class="columns-dropdown" ref="historyColumnsDropdown">
              <button
                type="button"
                class="btn-action btn-columns"
                @click.stop="showHistoryColumnsMenu = !showHistoryColumnsMenu"
                title="Mostrar ou esconder colunas"
              >
                <i class="fa-solid fa-columns"></i>
                Colunas
                <i class="fa-solid fa-caret-down columns-caret"></i>
              </button>
              <div v-if="showHistoryColumnsMenu" class="columns-menu" @click.stop>
                <div class="columns-menu-header">
                  <span>Colunas visíveis</span>
                  <button type="button" class="columns-reset" @click="resetHistoryVisibleColumns">
                    Restaurar
                  </button>
                </div>
                <label
                  v-for="col in historyToggleableColumns"
                  :key="col.key"
                  class="columns-menu-item"
                  :class="{ locked: col.locked }"
                >
                  <input
                    type="checkbox"
                    :checked="isHistoryColumnVisible(col.key)"
                    :disabled="col.locked"
                    @change="toggleHistoryColumn(col.key)"
                  />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>
            <button
              type="button"
              class="btn-action btn-print"
              @click="openPdfModal('history', 'print')"
              title="Imprimir relatório"
            >
              <i class="fa-solid fa-print"></i>
              Imprimir
            </button>
            <button
              type="button"
              class="btn-action btn-pdf"
              @click="openPdfModal('history', 'pdf')"
              title="Exportar para PDF"
            >
              <i class="fa-solid fa-file-pdf"></i>
              PDF
            </button>
            <button
              type="button"
              class="btn-action btn-excel"
              @click="exportHistoryToExcel"
              title="Exportar para Excel"
            >
              <i class="fa-solid fa-file-excel"></i>
              Excel
            </button>
          </div>
        </div>
        <div class="table-container" :style="tableZoomStyle">
          <table class="modern-table">
            <thead>
              <tr>
                <th v-if="isHistoryColumnVisible('collectorName')">Cobrador</th>
                <th v-if="isHistoryColumnVisible('period')">Período</th>
                <th v-if="isHistoryColumnVisible('liquidPrizePeriod')">LP período</th>
                <th v-if="isHistoryColumnVisible('previousOpen')">Saldo ant.</th>
                <th v-if="isHistoryColumnVisible('totalDue')">Total devido</th>
                <th v-if="isHistoryColumnVisible('amountPaid')">Pago</th>
                <th v-if="isHistoryColumnVisible('amountOpen')">Em aberto</th>
                <th v-if="isHistoryColumnVisible('status')">Status</th>
                <th v-if="isHistoryColumnVisible('createdAt')">Criado em</th>
                <th v-if="isHistoryColumnVisible('actions')">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in settlements"
                :key="item.id"
                class="data-row"
              >
                <td v-if="isHistoryColumnVisible('collectorName')" class="name-column">
                  {{ item.collectorName }}
                </td>
                <td v-if="isHistoryColumnVisible('period')">
                  {{ formatDateTime(item.periodStart) }} —
                  {{ formatDateTime(item.periodEnd) }}
                </td>
                <td v-if="isHistoryColumnVisible('liquidPrizePeriod')">
                  {{ setCurrencyString(item.liquidPrizePeriod) }}
                </td>
                <td v-if="isHistoryColumnVisible('previousOpen')">
                  {{ setCurrencyString(item.previousOpen) }}
                </td>
                <td v-if="isHistoryColumnVisible('totalDue')">
                  {{ setCurrencyString(item.totalDue) }}
                </td>
                <td v-if="isHistoryColumnVisible('amountPaid')" class="currency-positive">
                  {{ setCurrencyString(item.amountPaid) }}
                </td>
                <td
                  v-if="isHistoryColumnVisible('amountOpen')"
                  :class="
                    item.amountOpen > 0 ? 'currency-negative' : 'currency-positive'
                  "
                >
                  <strong>{{ setCurrencyString(item.amountOpen) }}</strong>
                </td>
                <td v-if="isHistoryColumnVisible('status')">
                  <span :class="['badge', statusClass(item.status)]">
                    {{ statusLabel(item.status) }}
                  </span>
                </td>
                <td v-if="isHistoryColumnVisible('createdAt')">
                  {{ formatDateTime(item.createdAt) }}
                </td>
                <td v-if="isHistoryColumnVisible('actions')">
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="btn-row-action btn-edit"
                      title="Detalhes"
                      @click="openDetail(item)"
                    >
                      <i class="fa-solid fa-eye"></i>
                    </button>
                    <button
                      v-if="item.amountOpen > 0"
                      type="button"
                      class="btn-row-action btn-pay"
                      title="Registrar recebimento"
                      @click="openPaymentModal(item)"
                    >
                      <i class="fa-solid fa-money-bill-wave"></i>
                    </button>
                    <button
                      type="button"
                      class="btn-row-action btn-receipt"
                      title="Comprovante"
                      @click="openReceipt(item)"
                    >
                      <i class="fa-solid fa-receipt"></i>
                    </button>
                    <button
                      v-if="canDeleteSettlement(item)"
                      type="button"
                      class="btn-row-action btn-delete"
                      title="Excluir fechamento"
                      :disabled="deletingSettlementId === item.id"
                      @click="deleteSettlement(item)"
                    >
                      <i
                        v-if="deletingSettlementId === item.id"
                        class="fa-solid fa-spinner fa-spin"
                      ></i>
                      <i v-else class="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!settlements.length && !loadingHistory">
                <td :colspan="historyVisibleColumnsCount" class="empty">
                  Nenhum fechamento encontrado
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal pagamento -->
    <div v-if="paymentModal.visible" class="modal-overlay" @click="closePaymentModal">
      <div class="modal-content payment-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-money-bill-wave"></i>
            Registrar Pagamento
            <template v-if="paymentModal.settlement">
              - {{ paymentModal.settlement.collectorName }}
            </template>
          </h3>
          <button type="button" class="modal-close" @click="closePaymentModal" aria-label="Fechar">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body" v-if="paymentModal.settlement">
          <div class="payment-form">
            <div class="payment-open-box">
              <span class="total-label">Em aberto:</span>
              <span class="total-value currency-negative">
                {{ setCurrencyString(paymentModal.settlement.amountOpen) }}
              </span>
            </div>

            <div class="form-grid">
              <div class="input-group">
                <label class="input-label required">
                  <i class="fa-solid fa-dollar-sign"></i>
                  Valor recebido
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="form-input"
                  v-model.number="paymentModal.amount"
                  placeholder="0,00"
                />
              </div>

              <div class="input-group">
                <label class="input-label required">
                  <i class="fa-solid fa-calendar-days"></i>
                  Data
                </label>
                <input type="date" class="form-input" v-model="paymentModal.date" />
              </div>

              <div class="input-group full-width">
                <label class="input-label">
                  <i class="fa-solid fa-note-sticky"></i>
                  Observações
                </label>
                <textarea
                  class="form-input"
                  rows="3"
                  v-model="paymentModal.notes"
                  placeholder="Observações sobre o pagamento (opcional)"
                ></textarea>
              </div>

              <div class="input-group full-width">
                <label class="input-label">
                  <i class="fa-solid fa-wallet"></i>
                  Resetar saldo da carteira dos vendedores
                </label>
                <select
                  class="form-input"
                  :value="paymentModal.resetSellerWallets ? 'true' : 'false'"
                  :disabled="walletResetModal.loading"
                  @change="onResetSellerWalletsChange"
                >
                  <option value="false">NÃO</option>
                  <option value="true">SIM</option>
                </select>
                <small class="filter-hint">
                  Se SIM, ao registrar o pagamento o saldo de cada vendedor do fechamento volta para o limite da carteira.
                </small>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="closePaymentModal">
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
          <button
            type="button"
            class="btn-primary"
            @click="submitPayment"
            :disabled="savingPayment || walletResetModal.loading"
          >
            <i
              :class="
                savingPayment
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-save'
              "
            ></i>
            {{ savingPayment ? "Processando..." : "Registrar Pagamento" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal confirmação reset carteira -->
    <div
      v-if="walletResetModal.visible"
      class="modal-overlay wallet-reset-overlay"
      @click="cancelWalletResetConfirm"
    >
      <div class="modal-content wallet-reset-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-wallet"></i>
            Confirmar reset de carteira
          </h3>
          <button
            type="button"
            class="modal-close"
            @click="cancelWalletResetConfirm"
            aria-label="Fechar"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="wallet-reset-intro">
            O saldo atual de cada vendedor abaixo voltará para o
            <strong>limite pré-definido</strong> da carteira ao registrar o pagamento.
          </p>
          <div v-if="walletResetModal.loading" class="wallet-reset-loading">
            <i class="fa-solid fa-spinner fa-spin"></i>
            Carregando saldos dos vendedores...
          </div>
          <div v-else-if="!walletResetModal.sellers.length" class="empty">
            Nenhum vendedor encontrado neste fechamento.
          </div>
          <div v-else class="table-container wallet-reset-table-wrap">
            <table class="modern-table wallet-reset-table">
              <thead>
                <tr>
                  <th>Vendedor</th>
                  <th>Saldo atual</th>
                  <th>Limite</th>
                  <th>Novo saldo</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="seller in walletResetModal.sellers"
                  :key="seller.id"
                >
                  <td class="name-column">{{ seller.name }}</td>
                  <td
                    :class="
                      seller.walletBalance < seller.walletLimit
                        ? 'currency-negative'
                        : 'currency-positive'
                    "
                  >
                    {{ setCurrencyString(seller.walletBalance) }}
                  </td>
                  <td>{{ setCurrencyString(seller.walletLimit) }}</td>
                  <td class="currency-positive">
                    <strong>{{ setCurrencyString(seller.walletLimit) }}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn-secondary"
            @click="cancelWalletResetConfirm"
            :disabled="walletResetModal.loading"
          >
            <i class="fa-solid fa-times"></i>
            Não
          </button>
          <button
            type="button"
            class="btn-primary"
            @click="confirmWalletReset"
            :disabled="walletResetModal.loading || !walletResetModal.sellers.length"
          >
            <i class="fa-solid fa-check"></i>
            Sim, resetar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal detalhe -->
    <div v-if="detailModal.visible" class="modal-overlay" @click="closeDetail">
      <div class="modal-content detail-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-file-invoice"></i>
            Detalhe do fechamento
          </h3>
          <button type="button" class="modal-close" @click="closeDetail" aria-label="Fechar">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="detailModal.settlement">
          <div class="detail-grid">
            <div>
              <span class="muted">Cobrador</span>
              <div>
                <strong>{{ detailModal.settlement.collectorName }}</strong>
              </div>
            </div>
            <div>
              <span class="muted">Período</span>
              <div>
                {{ formatDateTime(detailModal.settlement.periodStart) }} —
                {{ formatDateTime(detailModal.settlement.periodEnd) }}
              </div>
            </div>
            <div>
              <span class="muted">Total devido</span>
              <div>{{ setCurrencyString(detailModal.settlement.totalDue) }}</div>
            </div>
            <div>
              <span class="muted">Pago / Aberto</span>
              <div>
                {{ setCurrencyString(detailModal.settlement.amountPaid) }} /
                <strong
                  :class="
                    detailModal.settlement.amountOpen > 0
                      ? 'currency-negative'
                      : 'currency-positive'
                  "
                >
                  {{ setCurrencyString(detailModal.settlement.amountOpen) }}
                </strong>
              </div>
            </div>
          </div>

          <h4 class="section-title">Vendedores (snapshot)</h4>
          <div class="table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Vendedor</th>
                  <th>Saldo anterior</th>
                  <th>Vendas</th>
                  <th>Líquido</th>
                  <th>LP</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="s in detailModal.settlement.sellers || []"
                  :key="s.sellerId"
                  class="data-row"
                >
                  <td class="name-column">{{ s.sellerName }}</td>
                  <td>{{ setCurrencyString(s.previousBalance) }}</td>
                  <td>
                    <span
                      v-if="Number(s.sales || 0) > 0"
                      class="currency-value currency-positive clickable-value"
                      title="Clique para ver as vendas"
                      @click="openSalesModal(s, detailModal.settlement)"
                    >
                      {{ setCurrencyString(s.sales) }}
                    </span>
                    <span v-else>{{ setCurrencyString(s.sales) }}</span>
                  </td>
                  <td>{{ setCurrencyString(s.liquid) }}</td>
                  <td>{{ setCurrencyString(s.liquidPrize) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="(detailModal.settlement.sellers || []).length">
                <tr>
                  <td class="name-column"><strong>Total:</strong></td>
                  <td>
                    <strong>{{ setCurrencyString(sumSellersField(detailModal.settlement.sellers, "previousBalance")) }}</strong>
                  </td>
                  <td>
                    <strong>{{ setCurrencyString(sumSellersField(detailModal.settlement.sellers, "sales")) }}</strong>
                  </td>
                  <td>
                    <strong>{{ setCurrencyString(sumSellersField(detailModal.settlement.sellers, "liquid")) }}</strong>
                  </td>
                  <td>
                    <strong>{{ setCurrencyString(sumSellersField(detailModal.settlement.sellers, "liquidPrize")) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <h4 class="section-title">Recebimentos</h4>
          <div class="table-container">
            <table class="modern-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Valor</th>
                  <th>Registrado por</th>
                  <th>Obs.</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="p in detailModal.settlement.payments || []"
                  :key="p.id"
                  class="data-row"
                >
                  <td>{{ formatDate(p.paymentDate) }}</td>
                  <td class="currency-positive">
                    {{ setCurrencyString(p.amount) }}
                  </td>
                  <td>{{ p.registeredByUserName }}</td>
                  <td>{{ p.notes || "—" }}</td>
                </tr>
                <tr v-if="!(detailModal.settlement.payments || []).length">
                  <td colspan="4" class="empty">Nenhum recebimento ainda</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal comprovante -->
    <div v-if="receiptModal.visible" class="modal-overlay" @click="closeReceipt">
      <div class="modal-content receipt-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-receipt"></i>
            Comprovante
          </h3>
          <button type="button" class="modal-close" @click="closeReceipt" aria-label="Fechar">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body receipt-modal-body" v-if="receiptModal.settlement">
          <div ref="receiptTicket" class="receipt-ticket">
            <img
              :src="receiptLogoSrc"
              alt="Logo"
              class="receipt-logo"
            />

            <div class="receipt-dash"></div>
            <div class="receipt-row">
              <span>D: {{ formatDate(receiptModal.settlement.createdAt) }}</span>
              <span>H: {{ formatReceiptClock(receiptModal.settlement.createdAt) }}</span>
            </div>
            <div class="receipt-row">
              <span>Ref: {{ shortReceiptRef(receiptModal.settlement.id) }}</span>
              <span>{{ statusLabel(receiptModal.settlement.status) }}</span>
            </div>
            <div class="receipt-dash"></div>

            <div class="receipt-center receipt-title">COMPROVANTE</div>
            <div class="receipt-center receipt-subtitle">FECHAMENTO BANCA → COBRADOR</div>
            <div class="receipt-center">
              {{ receiptPeriodLabel(receiptModal.settlement) }}
            </div>
            <div class="receipt-dash"></div>

            <div class="receipt-row">
              <span>Cobrador</span>
              <span>{{ receiptModal.settlement.collectorName || "—" }}</span>
            </div>
            <div v-if="receiptModal.settlement.regionName" class="receipt-row">
              <span>Região</span>
              <span>{{ receiptModal.settlement.regionName }}</span>
            </div>
            <div class="receipt-row">
              <span>Período</span>
              <span>
                {{ formatDate(receiptModal.settlement.periodStart) }} -
                {{ formatDate(receiptModal.settlement.periodEnd) }}
              </span>
            </div>
            <div class="receipt-dash"></div>

            <div class="receipt-row">
              <span>Saldo anterior</span>
              <span>{{ formatReceiptMoney(receiptModal.settlement.previousOpen) }}</span>
            </div>
            <div class="receipt-dash"></div>

            <div class="receipt-center">VENDAS</div>
            <div
              v-for="s in receiptModal.settlement.sellers || []"
              :key="s.sellerId"
              class="receipt-seller"
            >
              <div class="receipt-row">
                <span>{{ s.sellerName }}</span>
                <span>{{ formatReceiptMoney(s.sales) }}</span>
              </div>
            </div>
            <div
              v-if="!(receiptModal.settlement.sellers || []).length"
              class="receipt-center muted-receipt"
            >
              Sem movimento no período
            </div>
            <div class="receipt-dash-short"></div>
            <div class="receipt-row">
              <span>Total vendas</span>
              <span>{{ formatReceiptMoney(sumSellersField(receiptModal.settlement.sellers, "sales")) }}</span>
            </div>
            <div class="receipt-dash"></div>

            <div class="receipt-row">
              <span>Comissão</span>
              <span>{{ formatReceiptCommission(receiptModal.settlement.sellers) }}</span>
            </div>
            <div class="receipt-row">
              <span>Prêmios</span>
              <span>{{ formatReceiptMoney(sumSellersField(receiptModal.settlement.sellers, "prizes")) }}</span>
            </div>
            <div class="receipt-row">
              <span>Líquido</span>
              <span>{{ formatReceiptMoney(sumSellersField(receiptModal.settlement.sellers, "liquid")) }}</span>
            </div>
            <div class="receipt-row receipt-strong">
              <span>LP</span>
              <span>{{ formatReceiptMoney(receiptModal.settlement.liquidPrizePeriod) }}</span>
            </div>
            <div class="receipt-dash"></div>

            <div class="receipt-row">
              <span>Total devido</span>
              <span>{{ formatReceiptMoney(receiptModal.settlement.totalDue) }}</span>
            </div>
            <div class="receipt-row">
              <span>Pago</span>
              <span>{{ formatReceiptMoney(receiptModal.settlement.amountPaid) }}</span>
            </div>
            <div class="receipt-row receipt-strong">
              <span>Saldo atual</span>
              <span>{{ formatReceiptMoney(receiptModal.settlement.amountOpen) }}</span>
            </div>
            <div class="receipt-dash"></div>

            <template v-if="(receiptModal.settlement.payments || []).length">
              <div class="receipt-center">RECEBIMENTOS</div>
              <div
                v-for="p in receiptModal.settlement.payments"
                :key="p.id"
                class="receipt-row"
              >
                <span>{{ formatDate(p.paymentDate) }}</span>
                <span>{{ formatReceiptMoney(p.amount) }}</span>
              </div>
              <div class="receipt-dash"></div>
            </template>

            <div v-if="receiptModal.settlement.createdByUserName" class="receipt-row">
              <span>Lançado por</span>
              <span>{{ receiptModal.settlement.createdByUserName }}</span>
            </div>
            <div class="receipt-center receipt-foot">
              COMPROVANTE DE FECHAMENTO
              <br />
              Documento sem valor fiscal
            </div>
          </div>
        </div>

        <div class="modal-footer receipt-actions">
          <button type="button" class="btn-secondary" @click="printReceipt">
            <i class="fa-solid fa-print"></i>
            Imprimir
          </button>
          <button type="button" class="btn-success" @click="shareReceiptWhatsApp">
            <i class="fa-brands fa-whatsapp"></i>
            WhatsApp
          </button>
          <button type="button" class="btn-primary" @click="shareReceipt">
            <i class="fa-solid fa-share-nodes"></i>
            Enviar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal PDF/Imprimir: todos ou específico -->
    <div v-if="pdfModal.visible" class="modal-overlay" @click="closePdfModal">
      <div class="modal-content pdf-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i
              class="fa-solid"
              :class="pdfModal.action === 'print' ? 'fa-print' : 'fa-file-pdf'"
            ></i>
            {{ pdfModal.action === "print" ? "Imprimir" : "Gerar PDF" }}
          </h3>
          <button type="button" class="modal-close" @click="closePdfModal" aria-label="Fechar">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="pdf-modal-hint">
            Escolha se
            {{ pdfModal.action === "print" ? "a impressão inclui" : "o PDF inclui" }}
            todos os registros ou só um específico.
          </p>
          <div class="pdf-scope-options">
            <label class="pdf-scope-option">
              <input type="radio" value="all" v-model="pdfModal.scope" />
              <span>Todos</span>
            </label>
            <label class="pdf-scope-option">
              <input type="radio" value="selected" v-model="pdfModal.scope" />
              <span>Selecionado</span>
            </label>
          </div>
          <div v-if="pdfModal.scope === 'selected'" class="input-group">
            <label class="input-label">
              {{ pdfModal.source === "history" ? "Fechamento" : "Cobrador" }}
            </label>
            <select v-model="pdfModal.selectedId" class="form-input">
              <option
                v-for="opt in pdfModalOptions"
                :key="opt.id"
                :value="opt.id"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn-secondary" @click="closePdfModal">
            Cancelar
          </button>
          <button type="button" class="btn-primary" @click="confirmPdfExport">
            <i
              class="fa-solid"
              :class="pdfModal.action === 'print' ? 'fa-print' : 'fa-file-pdf'"
            ></i>
            {{ pdfModal.action === "print" ? "Imprimir" : "Gerar PDF" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de vendas do período (mesmo padrão do fechamento de vendedores) -->
    <PrizeTicketsModal
      v-if="salesModal.visible"
      :sellerId="salesModal.sellerId"
      :regionId="salesModal.regionId"
      :regionName="salesModal.entityName"
      :dateBegin="salesModal.dateBegin"
      :dateEnd="salesModal.dateEnd"
      mode="sales"
      @close="closeSalesModal"
    />
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import mainService from "@/services/mainService";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import MultiSelect from "@/components/utils/MultiSelect.vue";
import PrizeTicketsModal from "@/components/PrizeTicketsModal.vue";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import logoLightFallback from "@/assets/light.png";
import { getCachedLoginSettings } from "@/services/settingsLogoCache";

const PREVIEW_COLUMNS_STORAGE_KEY = "viewEntriesCollectors.previewVisibleColumns";
const HISTORY_COLUMNS_STORAGE_KEY = "viewEntriesCollectors.historyVisibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "viewEntriesCollectors.tableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_PREVIEW_COLUMNS = {
  sellerName: true,
  previousBalance: true,
  sales: true,
  commission: true,
  liquid: true,
  prizes: true,
  liquidPrize: true,
};

const DEFAULT_HISTORY_COLUMNS = {
  collectorName: true,
  period: true,
  liquidPrizePeriod: true,
  previousOpen: true,
  totalDue: true,
  amountPaid: true,
  amountOpen: true,
  status: true,
  createdAt: true,
  actions: true,
};

function todayYmd() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthStartYmd() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
}

export default {
  name: "ViewEntriesCollectors",
  components: { MultiSelect, PrizeTicketsModal },
  data() {
    return {
      selectedTab: 1,
      currentUser: null,
      availableCollectors: [],
      selectedCollectorsObjects: [],
      availableSellers: [],
      selectedSellersObjects: [],
      historyCollectorsObjects: [],
      loadingSellers: false,
      bancas: [],
      regions: [],
      userResponsibleRegions: [],
      selectedRegionSnapshot: null,
      selectedBancaSnapshot: null,
      periodStart: monthStartYmd(),
      periodEnd: todayYmd(),
      lastSettlementByCollector: {},
      loadingLastSettlements: false,
      previews: [],
      loadingPreview: false,
      savingSettlementId: null,
      historyStatus: "",
      settlements: [],
      loadingHistory: false,
      deletingSettlementId: null,
      salesModal: {
        visible: false,
        sellerId: null,
        regionId: null,
        entityName: "",
        dateBegin: "",
        dateEnd: "",
      },
      paymentModal: {
        visible: false,
        settlement: null,
        amount: 0,
        date: todayYmd(),
        notes: "",
        resetSellerWallets: false,
      },
      walletResetModal: {
        visible: false,
        loading: false,
        sellers: [],
      },
      savingPayment: false,
      detailModal: {
        visible: false,
        settlement: null,
      },
      receiptModal: {
        visible: false,
        settlement: null,
      },
      receiptLogoUrl: "",
      pdfModal: {
        visible: false,
        source: "preview",
        action: "pdf",
        scope: "all",
        selectedId: "",
      },
      showPreviewColumnsMenu: false,
      showHistoryColumnsMenu: false,
      previewVisibleColumns: { ...DEFAULT_PREVIEW_COLUMNS },
      historyVisibleColumns: { ...DEFAULT_HISTORY_COLUMNS },
      tableZoom: 100,
    };
  },
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },
    receiptLogoSrc() {
      return this.receiptLogoUrl || logoLightFallback;
    },
    pdfModalOptions() {
      if (this.pdfModal.source === "history") {
        return (this.settlements || []).map((item) => ({
          id: String(item.id),
          label: `${item.collectorName || "Cobrador"} (${this.formatDateTime(
            item.periodStart
          )} — ${this.formatDateTime(item.periodEnd)})`,
        }));
      }
      return (this.previews || []).map((preview) => ({
        id: String(preview.collectorId),
        label: `${preview.collectorName || "Cobrador"}${
          preview.regionName ? ` — ${preview.regionName}` : ""
        }`,
      }));
    },
    previewToggleableColumns() {
      return [
        { key: "sellerName", label: "Vendedor", locked: true },
        { key: "previousBalance", label: "Saldo anterior" },
        { key: "sales", label: "Vendas" },
        { key: "commission", label: "Comissão" },
        { key: "liquid", label: "Líquido" },
        { key: "prizes", label: "Prêmios" },
        { key: "liquidPrize", label: "LP" },
      ];
    },
    historyToggleableColumns() {
      return [
        { key: "collectorName", label: "Cobrador", locked: true },
        { key: "period", label: "Período" },
        { key: "liquidPrizePeriod", label: "LP período" },
        { key: "previousOpen", label: "Saldo ant." },
        { key: "totalDue", label: "Total devido" },
        { key: "amountPaid", label: "Pago" },
        { key: "amountOpen", label: "Em aberto" },
        { key: "status", label: "Status" },
        { key: "createdAt", label: "Criado em" },
        { key: "actions", label: "Ações" },
      ];
    },
    previewVisibleColumnsCount() {
      return this.previewToggleableColumns.filter((c) =>
        this.isPreviewColumnVisible(c.key)
      ).length;
    },
    historyVisibleColumnsCount() {
      return this.historyToggleableColumns.filter((c) =>
        this.isHistoryColumnVisible(c.key)
      ).length;
    },
    showManualPeriodDates() {
      const selected = this.selectedCollectorsObjects || [];
      if (!selected.length) return true;
      // Mostra datas manuais se algum cobrador selecionado ainda não tem fechamento
      return selected.some(
        (c) => !this.lastSettlementByCollector[String(c.id)]
      );
    },
    autoPeriodRangeLabel() {
      const selected = this.selectedCollectorsObjects || [];
      const lasts = selected
        .map((c) => this.lastSettlementByCollector[String(c.id)])
        .filter(Boolean);
      if (!lasts.length) return "—";

      const oldestEnd = lasts
        .map((s) => s.periodEnd)
        .filter(Boolean)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];

      return `${this.formatDateTime(oldestEnd)} — agora (na geração)`;
    },
    previewTotals() {
      if (!this.previews.length) return null;
      return {
        liquidPrizePeriod: this.previews.reduce(
          (s, p) => s + (Number(p.liquidPrizePeriod) || 0),
          0
        ),
        previousOpen: this.previews.reduce(
          (s, p) => s + (Number(p.previousOpen) || 0),
          0
        ),
        totalDue: this.previews.reduce(
          (s, p) => s + (Number(p.totalDue) || 0),
          0
        ),
      };
    },
    currentSelectedRegion() {
      return this.selectedRegionSnapshot || this.readSelectedRegionFromStorage();
    },
    currentSelectedBanca() {
      return this.selectedBancaSnapshot || this.readSelectedBancaFromStorage();
    },
    isAdmin() {
      return (
        this.currentUser &&
        Number(this.currentUser.role) === ROLES.ADMIN
      );
    },
    isBanca() {
      return (
        this.currentUser &&
        Number(this.currentUser.role) === ROLES.BANCA
      );
    },
    isGerente() {
      return (
        this.currentUser &&
        Number(this.currentUser.role) === ROLES.GERENTE
      );
    },
    isCobrador() {
      return (
        this.currentUser &&
        Number(this.currentUser.role) === ROLES.COBRADOR
      );
    },
  },
  watch: {
    selectedCollectorsObjects: {
      handler() {
        this.loadSellersForSelectedCollectors();
        this.refreshLastSettlementsForSelection();
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      this.currentUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      this.currentUser = null;
    }

    this.loadPreviewVisibleColumns();
    this.loadHistoryVisibleColumns();
    this.loadTableZoom();
    this.loadReceiptLogo();

    this.syncSelectedRegionSnapshot();
    this.syncSelectedBancaSnapshot();

    if (this.currentUser && !this.isAdmin) {
      await this.loadUserResponsibleRegions();
    }

    await this.loadBancas();
    await this.loadRegions();

    if (this.isBanca) {
      await this.loadBancaRegions();
    }

    await this.loadCollectors();

    window.addEventListener("storage", this.onHeaderFilterStorage);
    window.addEventListener("regionChanged", this.onHeaderFilterChanged);
    window.addEventListener("regionAutoSelected", this.onHeaderFilterChanged);
    window.addEventListener("bancaChanged", this.onHeaderFilterChanged);
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  beforeUnmount() {
    window.removeEventListener("storage", this.onHeaderFilterStorage);
    window.removeEventListener("regionChanged", this.onHeaderFilterChanged);
    window.removeEventListener("regionAutoSelected", this.onHeaderFilterChanged);
    window.removeEventListener("bancaChanged", this.onHeaderFilterChanged);
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  methods: {
    readSelectedRegionFromStorage() {
      try {
        const savedRegion = localStorage.getItem("currentRegion");
        return savedRegion ? JSON.parse(savedRegion) : null;
      } catch {
        return null;
      }
    },
    readSelectedBancaFromStorage() {
      try {
        const savedBanca = localStorage.getItem("selectedBanca");
        return savedBanca ? JSON.parse(savedBanca) : null;
      } catch {
        return null;
      }
    },
    syncSelectedRegionSnapshot() {
      this.selectedRegionSnapshot = this.readSelectedRegionFromStorage();
    },
    syncSelectedBancaSnapshot() {
      this.selectedBancaSnapshot = this.readSelectedBancaFromStorage();
    },
    clearCollectorSelections() {
      this.selectedCollectorsObjects = [];
      this.selectedSellersObjects = [];
      this.availableSellers = [];
      this.historyCollectorsObjects = [];
      this.lastSettlementByCollector = {};
      this.previews = [];
      this.settlements = [];
    },
    onHeaderFilterStorage(e) {
      if (e.key === "currentRegion" || e.key === "selectedBanca") {
        this.onHeaderFilterChanged();
      }
    },
    onHeaderFilterChanged() {
      this.syncSelectedRegionSnapshot();
      this.syncSelectedBancaSnapshot();
      this.clearCollectorSelections();
      this.loadCollectors().then(() => {
        if (this.selectedTab === 2) {
          this.loadSettlements();
        }
      });
    },
    regionIdsForSelectedBanca() {
      if (!this.currentSelectedBanca) return [];
      return (this.regions || [])
        .filter(
          (r) =>
            String(r.bancaId) === String(this.currentSelectedBanca.id)
        )
        .map((r) => String(r.id));
    },
    matchesHeaderScope(regionId) {
      // Região do header tem prioridade; senão banca; senão sem restrição de header
      if (this.currentSelectedRegion) {
        return (
          String(regionId || "") === String(this.currentSelectedRegion.id)
        );
      }
      if (this.currentSelectedBanca) {
        return this.regionIdsForSelectedBanca().includes(
          String(regionId || "")
        );
      }
      return true;
    },
    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value || 0);
    },
    /** Horário local (Brasília) no formato da API: yyyy-MM-ddTHH:mm:ss (sem Z). */
    toLocalDateTimeIso(date = new Date()) {
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      );
    },
    formatDate(iso) {
      if (!iso) return "—";
      const d = String(iso).split("T")[0];
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    },
    formatDateTime(iso) {
      if (!iso) return "—";
      try {
        const raw = String(iso).trim();
        // API / DB usam timestamp sem fuso (horário local). Não converter via UTC.
        const localMatch = raw.match(
          /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
        );
        const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
        if (localMatch && !hasOffset) {
          const [, y, mo, day, hh, mm, ss = "00"] = localMatch;
          return `${day}/${mo}/${y}, ${hh}:${mm}:${ss}`;
        }
        return new Date(iso).toLocaleString("pt-BR");
      } catch {
        return this.formatDate(iso);
      }
    },
    /** Normaliza bound do período para a API de bilhetes (mantém hora quando existir). */
    toSalesModalDateBound(value, isStart) {
      if (!value) {
        return isStart
          ? this.toApiDateStart(this.periodStart)
          : this.toApiDateEnd(this.periodEnd);
      }
      const raw = String(value).trim();
      if (raw.includes("T")) {
        return raw.length >= 19 ? raw.slice(0, 19) : raw;
      }
      return isStart ? this.toApiDateStart(raw) : this.toApiDateEnd(raw);
    },
    openSalesModal(seller, periodContext = null) {
      if (!seller || Number(seller.sales || 0) <= 0) return;

      const sellerId = seller.sellerId || seller.id;
      const fullSeller = (this.availableSellers || []).find(
        (s) => String(s.id) === String(sellerId)
      );
      const regionId =
        fullSeller?.regionId ||
        seller.regionId ||
        periodContext?.regionId ||
        null;

      this.salesModal = {
        visible: true,
        sellerId: sellerId ? String(sellerId) : null,
        regionId: regionId ? String(regionId) : null,
        entityName: seller.sellerName || fullSeller?.fullName || "Vendedor",
        dateBegin: this.toSalesModalDateBound(
          periodContext?.periodStart,
          true
        ),
        dateEnd: this.toSalesModalDateBound(periodContext?.periodEnd, false),
      };
    },
    closeSalesModal() {
      this.salesModal = {
        visible: false,
        sellerId: null,
        regionId: null,
        entityName: "",
        dateBegin: "",
        dateEnd: "",
      };
    },
    statusLabel(status) {
      if (status === "PAID") return "Quitado";
      if (status === "PARTIAL") return "Parcial";
      return "Em aberto";
    },
    statusClass(status) {
      if (status === "PAID") return "badge-ok";
      if (status === "PARTIAL") return "badge-warn";
      return "badge-off";
    },
    canDeleteSettlement(item) {
      if (!item) return false;
      // Não permite excluir fechamento pago (total ou parcial)
      if (item.status === "PAID" || item.status === "PARTIAL") return false;
      if (Number(item.amountPaid) > 0) return false;
      return true;
    },
    toApiDateStart(ymd) {
      return `${ymd}T00:00:00`;
    },
    toApiDateEnd(ymd) {
      return `${ymd}T23:59:59`;
    },
    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = (response.data || []).map((region) => region.id);
        if (
          this.currentUser.regionId &&
          !this.userResponsibleRegions.includes(this.currentUser.regionId)
        ) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch {
        this.userResponsibleRegions = [];
      }
    },
    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        const bancasForUser = this.bancas.filter(
          (banca) =>
            banca.responsibleUserIds &&
            banca.responsibleUserIds.includes(this.currentUser.id)
        );
        for (const banca of bancasForUser) {
          if (banca.regionIds && Array.isArray(banca.regionIds)) {
            banca.regionIds.forEach((regionId) => {
              if (!this.userResponsibleRegions.includes(regionId)) {
                this.userResponsibleRegions.push(regionId);
              }
            });
          }
        }
      } catch {
        // ignore
      }
    },
    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch {
        this.bancas = [];
      }
    },
    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
      } catch {
        this.regions = [];
      }
    },
    async loadCollectors() {
      try {
        if (!this.bancas.length) {
          await this.loadBancas();
        }
        if (!this.regions.length) {
          await this.loadRegions();
        }

        const response = await mainService.searchCollectors();
        let collectors = (response.data || []).map((collector) => {
          const collectorRegion = this.regions.find(
            (r) => String(r.id) === String(collector.regionId)
          );
          const collectorBanca = collectorRegion
            ? this.bancas.find(
                (b) => String(b.id) === String(collectorRegion.bancaId)
              )
            : null;

          return {
            id: collector.id,
            name: `${collector.username || ""} - ${collector.name || "Cobrador"}`.replace(
              /^ - /,
              ""
            ),
            username: collector.username,
            fullName: collector.name,
            regionId: collector.regionId,
            regionName: collector.regionName || collectorRegion?.name || null,
            bancaId: collectorBanca?.id || collectorRegion?.bancaId || null,
            bancaName: collectorBanca?.name || "-",
            active: collector.isActive,
          };
        });

        if (this.isCobrador) {
          collectors = collectors.filter(
            (collector) => String(collector.id) === String(this.currentUser.id)
          );
        } else {
          // BANCA / GERENTE: só regiões responsáveis
          if (!this.isAdmin) {
            collectors = collectors.filter((collector) =>
              this.userResponsibleRegions.some(
                (id) => String(id) === String(collector.regionId)
              )
            );
          }

          // Header (banca/região): vale para todos, inclusive ADMIN
          collectors = collectors.filter((collector) =>
            this.matchesHeaderScope(collector.regionId)
          );
        }

        this.availableCollectors = [...collectors].sort((a, b) =>
          (a.fullName || a.name || "").localeCompare(
            b.fullName || b.name || "",
            "pt-BR",
            { sensitivity: "base" }
          )
        );

        // Remove seleções que saíram do filtro atual
        const allowedIds = new Set(
          this.availableCollectors.map((c) => String(c.id))
        );
        this.selectedCollectorsObjects = (
          this.selectedCollectorsObjects || []
        ).filter((c) => allowedIds.has(String(c.id)));
        this.historyCollectorsObjects = (
          this.historyCollectorsObjects || []
        ).filter((c) => allowedIds.has(String(c.id)));

        if (this.isCobrador && this.availableCollectors.length > 0) {
          this.selectedCollectorsObjects = [this.availableCollectors[0]];
          this.historyCollectorsObjects = [this.availableCollectors[0]];
          await this.loadSellersForSelectedCollectors();
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.availableCollectors = [];
      }
    },
    filterSellersByUserAccess(sellers) {
      if (!sellers || !sellers.length) return [];

      let filtered = [...sellers];

      // Cobrador: apenas vendedores vinculados a ele
      if (this.isCobrador) {
        const collectorId = this.currentUser?.id;
        filtered = filtered.filter(
          (seller) => String(seller.collectorId) === String(collectorId)
        );
      }

      // Não-admin: apenas vendedores das regiões do usuário
      if (!this.isAdmin) {
        filtered = filtered.filter((seller) =>
          this.userResponsibleRegions.some(
            (id) => String(id) === String(seller.regionId)
          )
        );
      }

      // Header (banca/região): vale para todos, inclusive ADMIN
      filtered = filtered.filter((seller) =>
        this.matchesHeaderScope(seller.regionId)
      );

      return filtered;
    },
    filterSettlementsByAccess(settlements) {
      if (!settlements || !settlements.length) return [];

      let filtered = [...settlements];

      if (this.isCobrador) {
        filtered = filtered.filter(
          (s) => String(s.collectorId) === String(this.currentUser?.id)
        );
        return filtered;
      }

      if (!this.isAdmin) {
        filtered = filtered.filter((s) =>
          this.userResponsibleRegions.some(
            (id) => String(id) === String(s.regionId || s.RegionId)
          )
        );
      }

      // Header (banca/região): vale para todos, inclusive ADMIN
      filtered = filtered.filter((s) =>
        this.matchesHeaderScope(s.regionId || s.RegionId)
      );

      return filtered;
    },
    async loadSellersForSelectedCollectors() {
      const collectors = this.selectedCollectorsObjects || [];
      if (!collectors.length) {
        this.availableSellers = [];
        this.selectedSellersObjects = [];
        return;
      }

      this.loadingSellers = true;
      try {
        const batches = await Promise.all(
          collectors.map(async (collector) => {
            try {
              const response = await mainService.getSellersByCollector(
                collector.id
              );
              const list = Array.isArray(response.data) ? response.data : [];
              return list.map((seller) => ({
                id: seller.id,
                name: `${seller.login || seller.username || ""} - ${
                  seller.name || "Vendedor"
                }`.replace(/^ - /, ""),
                username: seller.login || seller.username,
                fullName: seller.name,
                collectorId: seller.collectorId || collector.id,
                regionId: seller.regionId || collector.regionId || null,
                active: seller.isActive,
              }));
            } catch {
              return [];
            }
          })
        );

        const map = new Map();
        batches.flat().forEach((seller) => {
          if (seller?.id) map.set(String(seller.id), seller);
        });

        const accessibleSellers = this.filterSellersByUserAccess([
          ...map.values(),
        ]);

        this.availableSellers = accessibleSellers.sort((a, b) =>
          (a.fullName || a.name || "").localeCompare(
            b.fullName || b.name || "",
            "pt-BR",
            { sensitivity: "base" }
          )
        );

        const allowedIds = new Set(
          this.availableSellers.map((s) => String(s.id))
        );
        this.selectedSellersObjects = (
          this.selectedSellersObjects || []
        ).filter((s) => allowedIds.has(String(s.id)));
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.availableSellers = [];
        this.selectedSellersObjects = [];
      } finally {
        this.loadingSellers = false;
      }
    },
    selectedSellerIdsForRequest() {
      const selected = this.selectedSellersObjects || [];
      if (!selected.length) return null;
      return selected.map((s) => s.id);
    },
    async refreshLastSettlementsForSelection() {
      const collectors = this.selectedCollectorsObjects || [];
      if (!collectors.length) {
        this.lastSettlementByCollector = {};
        return;
      }

      this.loadingLastSettlements = true;
      try {
        const nextMap = { ...this.lastSettlementByCollector };
        await Promise.all(
          collectors.map(async (collector) => {
            try {
              const response = await mainService.searchCollectorSettlements({
                collectorId: collector.id,
                status: null,
                includeInactive: false,
              });
              const list = Array.isArray(response.data) ? response.data : [];
              if (!list.length) {
                nextMap[String(collector.id)] = null;
                return;
              }
              list.sort((a, b) => {
                const byPeriod =
                  new Date(b.periodEnd).getTime() -
                  new Date(a.periodEnd).getTime();
                if (byPeriod !== 0) return byPeriod;
                return (
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
                );
              });
              nextMap[String(collector.id)] = list[0];
            } catch {
              nextMap[String(collector.id)] = null;
            }
          })
        );
        this.lastSettlementByCollector = nextMap;
      } finally {
        this.loadingLastSettlements = false;
      }
    },
    resolvePeriodForCollector(collectorId, periodEndIso = null) {
      const endIso = periodEndIso || this.toLocalDateTimeIso();
      const last = this.lastSettlementByCollector[String(collectorId)];
      if (last?.periodEnd) {
        const periodStart = last.periodEnd;
        const startMs = new Date(periodStart).getTime();
        let periodEnd = endIso;
        let endMs = new Date(periodEnd).getTime();

        // Último fechamento pode ter terminado no fim do dia (23:59:59).
        // Se "agora" ainda for antes disso, abre janela mínima válida para a API
        // e a tela carrega com zeros (sem movimento novo).
        if (
          !Number.isFinite(startMs) ||
          !Number.isFinite(endMs) ||
          startMs >= endMs
        ) {
          periodEnd = this.toLocalDateTimeIso(
            new Date((Number.isFinite(startMs) ? startMs : Date.now()) + 1000)
          );
        }

        return {
          periodStart,
          periodEnd,
          isAuto: true,
        };
      }

      // Primeiro fechamento: início = data escolhida 00:00, fim = agora (hora do clique)
      const periodStart = this.toApiDateStart(this.periodStart);
      let periodEnd = endIso;
      const startMs = new Date(periodStart).getTime();
      const endMs = new Date(periodEnd).getTime();
      if (
        !Number.isFinite(startMs) ||
        !Number.isFinite(endMs) ||
        startMs >= endMs
      ) {
        periodEnd = this.toLocalDateTimeIso(
          new Date((Number.isFinite(startMs) ? startMs : Date.now()) + 1000)
        );
      }

      return {
        periodStart,
        periodEnd,
        isAuto: false,
      };
    },
    buildZeroedSellerLines(collectorId, sellerIdsPayload = null) {
      return (this.availableSellers || [])
        .filter((s) => {
          if (String(s.collectorId) !== String(collectorId)) return false;
          if (sellerIdsPayload && sellerIdsPayload.length) {
            return sellerIdsPayload.some((id) => String(id) === String(s.id));
          }
          return true;
        })
        .map((s) => ({
          sellerId: s.id,
          sellerName: s.fullName || "Vendedor",
          login: s.username || "",
          isActive: s.active !== false,
          previousBalance: 0,
          sales: 0,
          commission: 0,
          liquid: 0,
          prizes: 0,
          liquidPrize: 0,
          credit: 0,
          debit: 0,
        }));
    },
    ensurePreviewSellers(preview, collector, sellerIdsPayload = null) {
      if (!preview) return null;

      const apiSellers = Array.isArray(preview.sellers) ? preview.sellers : [];
      preview.collectorId = preview.collectorId || collector.id;
      preview.collectorName =
        preview.collectorName ||
        collector.fullName ||
        collector.name ||
        "Cobrador";
      preview.liquidPrizePeriod = Number(preview.liquidPrizePeriod) || 0;
      preview.previousOpen = Number(preview.previousOpen) || 0;

      // Restringe vendedores do preview à região / vínculo do cobrador
      preview.sellers = this.filterSellersByUserAccess(
        apiSellers.map((seller) => ({
          ...seller,
          id: seller.sellerId || seller.id,
          collectorId: seller.collectorId || collector.id,
          regionId: seller.regionId || collector.regionId || null,
        }))
      ).map(({ id, ...rest }) => ({
        ...rest,
        sellerId: rest.sellerId || id,
      }));

      // Só monta linhas zeradas quando a API não trouxe vendedores
      if (!apiSellers.length) {
        preview.sellers = this.buildZeroedSellerLines(
          collector.id,
          sellerIdsPayload
        );
      } else if (preview.sellers.length) {
        // Recalcula LP do período a partir dos vendedores visíveis
        preview.liquidPrizePeriod = preview.sellers.reduce(
          (sum, s) => sum + (Number(s.liquidPrize) || 0),
          0
        );
      } else {
        preview.liquidPrizePeriod = 0;
      }

      preview.totalDue =
        Number(preview.previousOpen) + Number(preview.liquidPrizePeriod);

      return preview;
    },
    buildEmptyPreview(collector, period, sellerIdsPayload = null) {
      const previousOpen = Number(
        this.lastSettlementByCollector[String(collector.id)]?.amountOpen
      ) || 0;
      return this.ensurePreviewSellers(
        {
          collectorId: collector.id,
          collectorName: collector.fullName || collector.name || "Cobrador",
          regionName: collector.regionName || null,
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          liquidPrizePeriod: 0,
          previousOpen,
          totalDue: previousOpen,
          sellers: [],
        },
        collector,
        sellerIdsPayload
      );
    },
    async buildPreviewsForSelected(periodEndIso = null) {
      let selected = this.selectedCollectorsObjects || [];
      if (!selected.length) {
        toast.error("Selecione ao menos um cobrador.");
        return [];
      }

      // Cobrador só opera sobre si mesmo
      if (this.isCobrador) {
        selected = selected.filter(
          (c) => String(c.id) === String(this.currentUser.id)
        );
        if (!selected.length && this.availableCollectors.length) {
          selected = [this.availableCollectors[0]];
        }
      }

      if (!selected.length) {
        toast.error("Selecione ao menos um cobrador.");
        return [];
      }

      await Promise.all([
        this.refreshLastSettlementsForSelection(),
        this.loadSellersForSelectedCollectors(),
      ]);

      if (this.showManualPeriodDates && !this.periodStart) {
        toast.error("Informe a data de início.");
        return [];
      }

      const endIso = periodEndIso || this.toLocalDateTimeIso();
      const sellerIds = this.selectedSellerIdsForRequest();

      const results = await Promise.all(
        selected.map(async (c) => {
          let sellerIdsPayload = null;
          if (sellerIds && sellerIds.length) {
            const sellerIdsForCollector = sellerIds.filter((id) =>
              this.availableSellers.some(
                (s) =>
                  String(s.id) === String(id) &&
                  String(s.collectorId) === String(c.id)
              )
            );
            // Sem vendedor filtrado para este cobrador: ainda monta zerado
            sellerIdsPayload = sellerIdsForCollector.length
              ? sellerIdsForCollector
              : null;
          }

          const period = this.resolvePeriodForCollector(c.id, endIso);
          const startMs = new Date(period.periodStart).getTime();
          const endMs = new Date(period.periodEnd).getTime();
          if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) {
            return this.buildEmptyPreview(c, period, sellerIdsPayload);
          }

          try {
            const response = await mainService.previewCollectorSettlement({
              collectorId: c.id,
              periodStart: period.periodStart,
              periodEnd: period.periodEnd,
              commissionType: "BRUTO/LIQUIDO",
              liquidPrize: "SEM DESPESA",
              sellerIds: sellerIdsPayload,
            });
            const preview = this.ensurePreviewSellers(
              response?.data,
              c,
              sellerIdsPayload
            );
            if (preview) return preview;
            return this.buildEmptyPreview(c, period, sellerIdsPayload);
          } catch {
            // API falhou: ainda assim mostra a grade zerada
            return this.buildEmptyPreview(c, period, sellerIdsPayload);
          }
        })
      );

      return results.filter(Boolean);
    },
    async generateSettlements() {
      if (this.loadingPreview || this.savingSettlementId) return;

      this.loadingPreview = true;
      this.previews = [];
      try {
        const generatedAtIso = this.toLocalDateTimeIso();
        const previews = await this.buildPreviewsForSelected(generatedAtIso);
        this.previews = previews;

        if (!previews.length) {
          toast.info("Selecione ao menos um cobrador para consultar.");
          return;
        }

        toast.success(
          previews.length === 1
            ? "Consulta carregada."
            : `${previews.length} consultas carregadas.`
        );
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.loadingPreview = false;
      }
    },
    async createSettlement(preview) {
      if (!preview) return;
      if (this.savingSettlementId) return;

      const periodLabel = `${this.formatDate(preview.periodStart)} — ${this.formatDate(
        preview.periodEnd
      )}`;
      const totalDue = this.setCurrencyString(preview.totalDue);

      this.$confirm({
        message: `Confirma o fechamento de ${
          preview.collectorName || "cobrador"
        } (${periodLabel}) no valor de ${totalDue}?`,
        button: { no: "Não", yes: "Sim, criar" },
        callback: async (confirm) => {
          if (!confirm) return;
          await this.executeCreateSettlement(preview);
        },
      });
    },
    async executeCreateSettlement(preview) {
      if (!preview) return;
      this.savingSettlementId = preview.collectorId;
      try {
        const generatedAtIso = this.toLocalDateTimeIso();
        await this.refreshLastSettlementsForSelection();
        const period = this.resolvePeriodForCollector(
          preview.collectorId,
          generatedAtIso
        );

        const sellerIds =
          (this.selectedSellersObjects || []).length > 0
            ? (preview.sellers || []).map((s) => s.sellerId)
            : null;

        const response = await mainService.createCollectorSettlement({
          collectorId: preview.collectorId,
          // Sempre usa o período resolvido no momento do clique (fim = agora)
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          notes: null,
          createdByUserId: this.currentUser?.id || null,
          commissionType: "BRUTO/LIQUIDO",
          liquidPrize: "SEM DESPESA",
          sellerIds: sellerIds && sellerIds.length ? sellerIds : null,
        });
        const settlement = response?.data || null;
        toast.success(`Fechamento de ${preview.collectorName} efetuado!`);
        this.previews = this.previews.filter(
          (p) => p.collectorId !== preview.collectorId
        );
        this.selectedTab = 2;
        const match = this.availableCollectors.find(
          (c) => String(c.id) === String(preview.collectorId)
        );
        if (match) {
          this.historyCollectorsObjects = [match];
        }
        await this.refreshLastSettlementsForSelection();
        await this.loadSettlements();

        const created =
          settlement?.id
            ? settlement
            : this.settlements.find(
                (s) => String(s.collectorId) === String(preview.collectorId)
              );
        if (created && Number(created.amountOpen) > 0) {
          this.openPaymentModal(created);
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.savingSettlementId = null;
      }
    },
    switchToHistorico() {
      this.selectedTab = 2;
      if (!this.settlements.length) {
        this.loadSettlements();
      }
    },
    async loadSettlements() {
      this.loadingHistory = true;
      try {
        const selected = this.historyCollectorsObjects || [];
        let list = [];
        if (!selected.length) {
          const response = await mainService.searchCollectorSettlements({
            collectorId: null,
            status: this.historyStatus || null,
            includeInactive: false,
          });
          list = Array.isArray(response.data) ? response.data : [];
        } else {
          const batches = await Promise.all(
            selected.map(async (c) => {
              const response = await mainService.searchCollectorSettlements({
                collectorId: c.id,
                status: this.historyStatus || null,
                includeInactive: false,
              });
              return Array.isArray(response.data) ? response.data : [];
            })
          );
          const map = new Map();
          batches.flat().forEach((item) => {
            if (item?.id) map.set(item.id, item);
          });
          list = [...map.values()];
        }

        this.settlements = this.filterSettlementsByAccess(list).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.settlements = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    openDetail(item) {
      this.detailModal = { visible: true, settlement: item };
    },
    closeDetail() {
      this.detailModal = { visible: false, settlement: null };
    },
    async loadReceiptLogo() {
      try {
        const cached = getCachedLoginSettings();
        if (cached?.logoLight) {
          this.receiptLogoUrl = cached.logoLight;
        }
        const response = await mainService.getActiveSettings();
        if (response.data?.logoLight) {
          this.receiptLogoUrl = response.data.logoLight;
        }
      } catch {
        if (!this.receiptLogoUrl) this.receiptLogoUrl = "";
      }
    },
    openReceipt(item) {
      if (!item) return;
      this.receiptModal = { visible: true, settlement: item };
    },
    closeReceipt() {
      this.receiptModal = { visible: false, settlement: null };
    },
    formatReceiptMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatReceiptCommission(sellers) {
      const total = this.sumSellersField(sellers, "commission");
      if (!total) return this.formatReceiptMoney(0);
      return `-${this.formatReceiptMoney(total)}`;
    },
    formatReceiptClock(iso) {
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "--:--:--";
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      } catch {
        return "--:--:--";
      }
    },
    shortReceiptRef(id) {
      const raw = String(id || "").replace(/-/g, "");
      return raw.slice(0, 8).toUpperCase() || "--------";
    },
    weekdayFromIso(iso) {
      const d = String(iso || "").split("T")[0];
      const [y, m, day] = d.split("-").map(Number);
      if (!y || !m || !day) return "";
      const date = new Date(y, m - 1, day);
      return ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][date.getDay()] || "";
    },
    receiptPeriodLabel(item) {
      if (!item) return "";
      const start = String(item.periodStart || "").split("T")[0];
      const end = String(item.periodEnd || "").split("T")[0];
      if (start && start === end) {
        return `${this.weekdayFromIso(item.periodStart)}, ${this.formatDate(item.periodStart)}`;
      }
      return `${this.formatDate(item.periodStart)} - ${this.formatDate(item.periodEnd)}`;
    },
    buildReceiptText(item) {
      if (!item) return "";
      const line = "------------------------";
      const row = (left, right) => {
        const l = String(left);
        const r = String(right);
        const spaces = Math.max(1, 24 - l.length - r.length);
        return `${l}${" ".repeat(spaces)}${r}`;
      };
      const sellers = item.sellers || [];
      const lines = [
        "COMPROVANTE DE FECHAMENTO",
        "Banca -> Cobrador",
        line,
        `D: ${this.formatDate(item.createdAt)}  H: ${this.formatReceiptClock(item.createdAt)}`,
        `Ref: ${this.shortReceiptRef(item.id)}  ${this.statusLabel(item.status)}`,
        line,
        this.receiptPeriodLabel(item),
        row("Cobrador", item.collectorName || "-"),
      ];
      if (item.regionName) lines.push(row("Regiao", item.regionName));
      lines.push(
        row("Periodo", `${this.formatDate(item.periodStart)} - ${this.formatDate(item.periodEnd)}`),
        line,
        row("Saldo anterior", this.formatReceiptMoney(item.previousOpen)),
        line,
        "VENDAS"
      );
      if (!sellers.length) {
        lines.push("Sem movimento no periodo");
      } else {
        sellers.forEach((s) => {
          lines.push(
            row(s.sellerName || "Vendedor", this.formatReceiptMoney(s.sales))
          );
        });
      }
      lines.push(
        row("Total vendas", this.formatReceiptMoney(this.sumSellersField(sellers, "sales"))),
        line,
        row("Comissao", this.formatReceiptCommission(sellers)),
        row("Premios", this.formatReceiptMoney(this.sumSellersField(sellers, "prizes"))),
        row("Liquido", this.formatReceiptMoney(this.sumSellersField(sellers, "liquid"))),
        row("LP", this.formatReceiptMoney(item.liquidPrizePeriod)),
        line,
        row("Total devido", this.formatReceiptMoney(item.totalDue)),
        row("Pago", this.formatReceiptMoney(item.amountPaid)),
        row("Saldo atual", this.formatReceiptMoney(item.amountOpen)),
        line
      );
      if ((item.payments || []).length) {
        lines.push("RECEBIMENTOS");
        item.payments.forEach((p) => {
          lines.push(row(this.formatDate(p.paymentDate), this.formatReceiptMoney(p.amount)));
        });
        lines.push(line);
      }
      if (item.createdByUserName) {
        lines.push(row("Lancado por", item.createdByUserName));
      }
      lines.push("Documento sem valor fiscal");
      return lines.join("\n");
    },
    printReceipt() {
      const ticket = this.$refs.receiptTicket;
      if (!ticket) return;
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Permita pop-ups para imprimir o comprovante.");
        return;
      }
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Comprovante de Fechamento</title>
          <style>
            @page { margin: 8mm; size: 80mm auto; }
            body { margin: 0; background: #fff; }
            .receipt-ticket {
              width: 300px;
              margin: 0 auto;
              padding: 12px 10px 18px;
              font-family: "Courier New", Courier, monospace;
              color: #111;
              background: #fff;
              font-size: 13px;
              line-height: 1.35;
            }
            .receipt-logo { display: block; max-width: 180px; max-height: 64px; margin: 0 auto 8px; object-fit: contain; }
            .receipt-dash { border-top: 1px dashed #111; margin: 8px 0; }
            .receipt-dash-short { border-top: 1px dashed #111; margin: 4px 0 4px 40%; }
            .receipt-row { display: flex; justify-content: space-between; gap: 8px; }
            .receipt-center { text-align: center; }
            .receipt-title { font-weight: 700; letter-spacing: 1px; margin-top: 4px; }
            .receipt-subtitle { font-size: 11px; margin-bottom: 4px; }
            .receipt-strong { font-weight: 700; }
            .receipt-foot { font-size: 11px; margin-top: 8px; }
            .muted-receipt { font-size: 12px; }
            .receipt-seller { margin: 2px 0; }
          </style>
        </head>
        <body>${ticket.outerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 300);
    },
    shareReceiptWhatsApp() {
      const item = this.receiptModal.settlement;
      if (!item) return;
      const text = this.buildReceiptText(item);
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    },
    async shareReceipt() {
      const item = this.receiptModal.settlement;
      if (!item) return;
      const text = this.buildReceiptText(item);
      try {
        if (navigator.share) {
          await navigator.share({
            title: "Comprovante de Fechamento",
            text,
          });
          return;
        }
      } catch {
        /* usuário cancelou ou share indisponível */
      }
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Comprovante copiado. Cole para enviar.");
      } catch {
        this.shareReceiptWhatsApp();
      }
    },
    openPaymentModal(item) {
      this.paymentModal = {
        visible: true,
        settlement: item,
        amount: Number(item.amountOpen) || 0,
        date: todayYmd(),
        notes: "",
        resetSellerWallets: false,
      };
      this.walletResetModal = {
        visible: false,
        loading: false,
        sellers: [],
      };
    },
    closePaymentModal() {
      this.paymentModal = {
        visible: false,
        settlement: null,
        amount: 0,
        date: todayYmd(),
        notes: "",
        resetSellerWallets: false,
      };
      this.walletResetModal = {
        visible: false,
        loading: false,
        sellers: [],
      };
    },
    async onResetSellerWalletsChange(event) {
      const wantsReset = event?.target?.value === "true";

      // Mantém NÃO até o usuário confirmar no modal com a lista
      if (event?.target) {
        event.target.value = this.paymentModal.resetSellerWallets
          ? "true"
          : "false";
      }

      if (!wantsReset) {
        this.paymentModal.resetSellerWallets = false;
        this.walletResetModal = {
          visible: false,
          loading: false,
          sellers: [],
        };
        return;
      }

      await this.openWalletResetConfirmModal();
    },
    async openWalletResetConfirmModal() {
      const settlement = this.paymentModal.settlement;
      if (!settlement?.id) {
        toast.error("Fechamento inválido.");
        this.paymentModal.resetSellerWallets = false;
        return;
      }

      this.walletResetModal = {
        visible: true,
        loading: true,
        sellers: [],
      };

      try {
        let settlementSellers = Array.isArray(settlement.sellers)
          ? settlement.sellers
          : [];

        // Busca o fechamento completo se a lista de vendedores não veio na listagem
        if (!settlementSellers.length) {
          const detailRes = await mainService.getCollectorSettlementById(
            settlement.id
          );
          const detail = detailRes?.data || null;
          if (detail) {
            this.paymentModal.settlement = {
              ...settlement,
              ...detail,
            };
            settlementSellers = Array.isArray(detail.sellers)
              ? detail.sellers
              : [];
          }
        }

        const sellerIds = [
          ...new Set(
            settlementSellers
              .map((s) => s.sellerId || s.id)
              .filter(Boolean)
              .map((id) => String(id))
          ),
        ];

        if (!sellerIds.length) {
          this.walletResetModal.loading = false;
          this.walletResetModal.sellers = [];
          toast.warning("Nenhum vendedor encontrado neste fechamento.");
          return;
        }

        const results = await Promise.all(
          sellerIds.map(async (sellerId) => {
            try {
              const res = await mainService.getSellerById(sellerId);
              const seller = res?.data || null;
              if (!seller) return null;
              const snapshot = settlementSellers.find(
                (s) =>
                  String(s.sellerId || s.id) === String(sellerId)
              );
              return {
                id: seller.id,
                name:
                  seller.name ||
                  snapshot?.sellerName ||
                  snapshot?.name ||
                  "Vendedor",
                walletBalance: Number(
                  seller.walletBalance ?? seller.WalletBalance ?? 0
                ),
                walletLimit: Number(
                  seller.walletLimit ?? seller.WalletLimit ?? 0
                ),
              };
            } catch {
              return null;
            }
          })
        );

        this.walletResetModal.sellers = results
          .filter(Boolean)
          .sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", "pt-BR", {
              sensitivity: "base",
            })
          );
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.cancelWalletResetConfirm();
        return;
      } finally {
        this.walletResetModal.loading = false;
      }
    },
    confirmWalletReset() {
      if (!this.walletResetModal.sellers.length) {
        toast.warning("Nenhum vendedor para resetar.");
        return;
      }
      this.paymentModal.resetSellerWallets = true;
      this.walletResetModal = {
        visible: false,
        loading: false,
        sellers: this.walletResetModal.sellers,
      };
      toast.info(
        `Reset de carteira ativado para ${this.walletResetModal.sellers.length} vendedor(es).`
      );
    },
    cancelWalletResetConfirm() {
      this.paymentModal.resetSellerWallets = false;
      this.walletResetModal = {
        visible: false,
        loading: false,
        sellers: [],
      };
    },
    loadPreviewVisibleColumns() {
      try {
        const saved = localStorage.getItem(PREVIEW_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.previewVisibleColumns = { ...DEFAULT_PREVIEW_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.previewVisibleColumns = {
          ...DEFAULT_PREVIEW_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          sellerName: true,
        };
      } catch {
        this.previewVisibleColumns = { ...DEFAULT_PREVIEW_COLUMNS };
      }
    },
    savePreviewVisibleColumns() {
      localStorage.setItem(
        PREVIEW_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.previewVisibleColumns)
      );
    },
    loadHistoryVisibleColumns() {
      try {
        const saved = localStorage.getItem(HISTORY_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.historyVisibleColumns = { ...DEFAULT_HISTORY_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.historyVisibleColumns = {
          ...DEFAULT_HISTORY_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          collectorName: true,
        };
      } catch {
        this.historyVisibleColumns = { ...DEFAULT_HISTORY_COLUMNS };
      }
    },
    saveHistoryVisibleColumns() {
      localStorage.setItem(
        HISTORY_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.historyVisibleColumns)
      );
    },
    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (
          !Number.isNaN(saved) &&
          saved >= TABLE_ZOOM_MIN &&
          saved <= TABLE_ZOOM_MAX
        ) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },
    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },
    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },
    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },
    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },
    isPreviewColumnVisible(key) {
      return this.previewVisibleColumns[key] !== false;
    },
    togglePreviewColumn(key) {
      if (key === "sellerName") return;
      this.previewVisibleColumns = {
        ...this.previewVisibleColumns,
        [key]: !this.isPreviewColumnVisible(key),
      };
      this.savePreviewVisibleColumns();
    },
    resetPreviewVisibleColumns() {
      this.previewVisibleColumns = { ...DEFAULT_PREVIEW_COLUMNS };
      this.savePreviewVisibleColumns();
    },
    isHistoryColumnVisible(key) {
      return this.historyVisibleColumns[key] !== false;
    },
    toggleHistoryColumn(key) {
      if (key === "collectorName") return;
      this.historyVisibleColumns = {
        ...this.historyVisibleColumns,
        [key]: !this.isHistoryColumnVisible(key),
      };
      this.saveHistoryVisibleColumns();
    },
    resetHistoryVisibleColumns() {
      this.historyVisibleColumns = { ...DEFAULT_HISTORY_COLUMNS };
      this.saveHistoryVisibleColumns();
    },
    handleColumnsMenuOutsideClick(event) {
      const previewDropdown = this.$refs.previewColumnsDropdown;
      const historyDropdown = this.$refs.historyColumnsDropdown;
      if (
        this.showPreviewColumnsMenu &&
        previewDropdown &&
        !previewDropdown.contains(event.target)
      ) {
        this.showPreviewColumnsMenu = false;
      }
      if (
        this.showHistoryColumnsMenu &&
        historyDropdown &&
        !historyDropdown.contains(event.target)
      ) {
        this.showHistoryColumnsMenu = false;
      }
    },
    getPreviewExportHeaders() {
      const headers = [];
      if (this.isPreviewColumnVisible("sellerName")) headers.push("Vendedor");
      if (this.isPreviewColumnVisible("previousBalance")) headers.push("Saldo anterior");
      if (this.isPreviewColumnVisible("sales")) headers.push("Vendas");
      if (this.isPreviewColumnVisible("commission")) headers.push("Comissão");
      if (this.isPreviewColumnVisible("liquid")) headers.push("Líquido");
      if (this.isPreviewColumnVisible("prizes")) headers.push("Prêmios");
      if (this.isPreviewColumnVisible("liquidPrize")) headers.push("LP");
      return headers;
    },
    mapPreviewSellerRow(seller, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const row = [];
      if (this.isPreviewColumnVisible("sellerName")) {
        row.push(seller.sellerName || "");
      }
      if (this.isPreviewColumnVisible("previousBalance")) {
        row.push(money(seller.previousBalance));
      }
      if (this.isPreviewColumnVisible("sales")) row.push(money(seller.sales));
      if (this.isPreviewColumnVisible("commission")) row.push(money(seller.commission));
      if (this.isPreviewColumnVisible("liquid")) row.push(money(seller.liquid));
      if (this.isPreviewColumnVisible("prizes")) row.push(money(seller.prizes));
      if (this.isPreviewColumnVisible("liquidPrize")) row.push(money(seller.liquidPrize));
      return row;
    },
    sumSellersField(sellers, field) {
      return (sellers || []).reduce(
        (sum, seller) => sum + (Number(seller?.[field]) || 0),
        0
      );
    },
    sumPreviewField(preview, field) {
      return this.sumSellersField(preview?.sellers, field);
    },
    mapPreviewTotalRow(preview, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const row = [];
      if (this.isPreviewColumnVisible("sellerName")) row.push("Total:");
      if (this.isPreviewColumnVisible("previousBalance")) {
        row.push(money(this.sumPreviewField(preview, "previousBalance")));
      }
      if (this.isPreviewColumnVisible("sales")) {
        row.push(money(this.sumPreviewField(preview, "sales")));
      }
      if (this.isPreviewColumnVisible("commission")) {
        row.push(money(this.sumPreviewField(preview, "commission")));
      }
      if (this.isPreviewColumnVisible("liquid")) {
        row.push(money(this.sumPreviewField(preview, "liquid")));
      }
      if (this.isPreviewColumnVisible("prizes")) {
        row.push(money(this.sumPreviewField(preview, "prizes")));
      }
      if (this.isPreviewColumnVisible("liquidPrize")) {
        row.push(money(this.sumPreviewField(preview, "liquidPrize")));
      }
      return row;
    },
    getHistoryExportHeaders() {
      const headers = [];
      if (this.isHistoryColumnVisible("collectorName")) headers.push("Cobrador");
      if (this.isHistoryColumnVisible("period")) headers.push("Período");
      if (this.isHistoryColumnVisible("liquidPrizePeriod")) headers.push("LP período");
      if (this.isHistoryColumnVisible("previousOpen")) headers.push("Saldo ant.");
      if (this.isHistoryColumnVisible("totalDue")) headers.push("Total devido");
      if (this.isHistoryColumnVisible("amountPaid")) headers.push("Pago");
      if (this.isHistoryColumnVisible("amountOpen")) headers.push("Em aberto");
      if (this.isHistoryColumnVisible("status")) headers.push("Status");
      if (this.isHistoryColumnVisible("createdAt")) headers.push("Criado em");
      return headers;
    },
    mapHistoryRow(item, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const row = [];
      if (this.isHistoryColumnVisible("collectorName")) {
        row.push(item.collectorName || "");
      }
      if (this.isHistoryColumnVisible("period")) {
        row.push(
          `${this.formatDateTime(item.periodStart)} — ${this.formatDateTime(item.periodEnd)}`
        );
      }
      if (this.isHistoryColumnVisible("liquidPrizePeriod")) {
        row.push(money(item.liquidPrizePeriod));
      }
      if (this.isHistoryColumnVisible("previousOpen")) {
        row.push(money(item.previousOpen));
      }
      if (this.isHistoryColumnVisible("totalDue")) row.push(money(item.totalDue));
      if (this.isHistoryColumnVisible("amountPaid")) row.push(money(item.amountPaid));
      if (this.isHistoryColumnVisible("amountOpen")) row.push(money(item.amountOpen));
      if (this.isHistoryColumnVisible("status")) {
        row.push(this.statusLabel(item.status));
      }
      if (this.isHistoryColumnVisible("createdAt")) {
        row.push(this.formatDateTime(item.createdAt));
      }
      return row;
    },
    openPdfModal(source, action = "pdf") {
      const list = source === "history" ? this.settlements : this.previews;
      if (!list.length) {
        toast.warning(
          action === "print"
            ? "Não há dados para imprimir"
            : "Não há dados para exportar"
        );
        return;
      }
      const first = list[0];
      this.pdfModal = {
        visible: true,
        source,
        action: action === "print" ? "print" : "pdf",
        scope: "all",
        selectedId:
          source === "history" ? String(first.id) : String(first.collectorId),
      };
    },
    closePdfModal() {
      this.pdfModal = {
        visible: false,
        source: "preview",
        action: "pdf",
        scope: "all",
        selectedId: "",
      };
    },
    confirmPdfExport() {
      const isPrint = this.pdfModal.action === "print";

      if (this.pdfModal.source === "history") {
        const items =
          this.pdfModal.scope === "all"
            ? this.settlements
            : this.settlements.filter(
                (s) => String(s.id) === String(this.pdfModal.selectedId)
              );
        if (!items.length) {
          toast.warning("Selecione um fechamento.");
          return;
        }
        this.closePdfModal();
        if (isPrint) this.printHistoryReport(items);
        else this.exportHistoryToPDF(items);
        return;
      }

      const items =
        this.pdfModal.scope === "all"
          ? this.previews
          : this.previews.filter(
              (p) => String(p.collectorId) === String(this.pdfModal.selectedId)
            );
      if (!items.length) {
        toast.warning("Selecione um cobrador.");
        return;
      }
      this.closePdfModal();
      if (isPrint) this.printPreviewReport(items);
      else this.exportPreviewToPDF(items);
    },
    printPreviewReport(items = null) {
      const previews =
        Array.isArray(items) && items.length ? items : this.previews;
      if (!previews.length) {
        toast.warning("Não há dados para imprimir");
        return;
      }
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Não foi possível abrir a janela de impressão");
        return;
      }
      printWindow.document.write(this.generatePreviewPrintHTML(previews));
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },
    generatePreviewPrintHTML(items = null) {
      const previews =
        Array.isArray(items) && items.length ? items : this.previews;
      const headers = this.getPreviewExportHeaders()
        .map((h) => `<th>${h}</th>`)
        .join("");
      const colCount = this.getPreviewExportHeaders().length || 1;
      const sections = previews
        .map((preview) => {
          const sellers = preview.sellers || [];
          const rows = sellers
            .map(
              (seller) =>
                `<tr>${this.mapPreviewSellerRow(seller, true)
                  .map((cell) => `<td>${cell}</td>`)
                  .join("")}</tr>`
            )
            .join("");
          return `
            <section class="settlement-block">
              <h2>Vendedores de ${preview.collectorName || ""}${
                preview.regionName ? ` — ${preview.regionName}` : ""
              }</h2>
              <table>
                <thead><tr>${headers}</tr></thead>
                <tbody>${
                  rows ||
                  `<tr><td colspan="${colCount}">Sem vendedores</td></tr>`
                }</tbody>
                ${
                  sellers.length
                    ? `<tfoot><tr>${this.mapPreviewTotalRow(preview, true)
                        .map((cell) => `<td><strong>${cell}</strong></td>`)
                        .join("")}</tr></tfoot>`
                    : ""
                }
              </table>
            </section>
          `;
        })
        .join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Fechamento Banca → Cobrador</title>
          <style>
            @media print {
              @page { size: landscape; margin: 1cm; }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            h1 { text-align: center; color: #2c3e50; font-size: 18px; }
            h2 { margin: 22px 0 8px; color: #334155; font-size: 14px; }
            .report-info { margin-bottom: 16px; color: #666; font-size: 12px; }
            .settlement-block { page-break-inside: avoid; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 11px; }
            th { background: #e5e5e5; padding: 6px; border: 1px solid #ddd; }
            td { padding: 6px; border: 1px solid #ddd; text-align: center; }
            tr:nth-child(even) { background: #f9f9f9; }
            tfoot td { background: #e5e5e5; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Fechamento Banca → Cobrador</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.periodStart} até ${this.periodEnd}</p>
          </div>
          ${sections}
        </body>
        </html>
      `;
    },
    exportPreviewToPDF(items = null) {
      const previews = Array.isArray(items) && items.length ? items : this.previews;
      if (!previews.length) {
        toast.warning("Não há dados para exportar");
        return;
      }
      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        doc.setFontSize(16);
        doc.text("Fechamento Banca → Cobrador", 14, 15);
        doc.setFontSize(10);
        doc.text(`Período: ${this.periodStart} até ${this.periodEnd}`, 14, 22);

        const headers = this.getPreviewExportHeaders();
        let startY = 28;
        previews.forEach((preview, index) => {
          if (index > 0) startY = (doc.lastAutoTable?.finalY || startY) + 10;
          doc.setFontSize(11);
          doc.text(
            `Vendedores de ${preview.collectorName || ""}${
              preview.regionName ? ` — ${preview.regionName}` : ""
            }`,
            14,
            startY
          );
          const body = (preview.sellers || []).map((s) =>
            this.mapPreviewSellerRow(s, true)
          );
          autoTable(doc, {
            head: [headers],
            body,
            foot: body.length ? [this.mapPreviewTotalRow(preview, true)] : undefined,
            startY: startY + 4,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
            footStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0], fontStyle: "bold" },
            alternateRowStyles: { fillColor: [249, 249, 249] },
            margin: { left: 14, right: 14 },
          });
        });

        const suffix =
          previews.length === 1
            ? previews[0].collectorName || "Cobrador"
            : "Todos";
        doc.save(
          `Fechamento_Cobrador_${suffix}_${this.periodStart}_${this.periodEnd}.pdf`
        );
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error(error);
        toast.error("Erro ao exportar PDF");
      }
    },
    exportPreviewToExcel() {
      if (!this.previews.length) {
        toast.warning("Não há dados para exportar");
        return;
      }
      try {
        const headers = this.getPreviewExportHeaders();
        const wb = XLSX.utils.book_new();
        const allData = [
          ["Fechamento Banca → Cobrador"],
          [""],
          ["Período:", `${this.periodStart} até ${this.periodEnd}`],
          [
            "Data de geração:",
            jsFunctions.setDateTimeDisplay(new Date().toISOString()),
          ],
          [""],
        ];

        this.previews.forEach((preview) => {
          allData.push([
            `Vendedores de ${preview.collectorName || ""}${
              preview.regionName ? ` — ${preview.regionName}` : ""
            }`,
          ]);
          allData.push([
            "LP:",
            preview.liquidPrizePeriod,
            "Anterior:",
            preview.previousOpen,
            "A pagar:",
            preview.totalDue,
          ]);
          allData.push(headers);
          (preview.sellers || []).forEach((seller) => {
            allData.push(this.mapPreviewSellerRow(seller, false));
          });
          if ((preview.sellers || []).length) {
            allData.push(this.mapPreviewTotalRow(preview, false));
          }
          allData.push([""]);
        });

        const ws = XLSX.utils.aoa_to_sheet(allData);
        ws["!cols"] = headers.map(() => ({ wch: 16 }));
        XLSX.utils.book_append_sheet(wb, ws, "Consulta");
        XLSX.writeFile(
          wb,
          `Fechamento_Cobrador_${this.periodStart}_${this.periodEnd}.xlsx`
        );
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error(error);
        toast.error("Erro ao exportar Excel");
      }
    },
    mapHistorySellerRow(seller, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      return [
        seller.sellerName || "",
        money(seller.previousBalance),
        money(seller.sales),
        money(seller.commission),
        money(seller.liquid),
        money(seller.prizes),
        money(seller.liquidPrize),
      ];
    },
    mapHistorySellerTotalRow(item, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const sellers = item?.sellers || [];
      return [
        "Total:",
        money(this.sumSellersField(sellers, "previousBalance")),
        money(this.sumSellersField(sellers, "sales")),
        money(this.sumSellersField(sellers, "commission")),
        money(this.sumSellersField(sellers, "liquid")),
        money(this.sumSellersField(sellers, "prizes")),
        money(this.sumSellersField(sellers, "liquidPrize")),
      ];
    },
    getHistorySellerExportHeaders() {
      return [
        "Vendedor",
        "Saldo anterior",
        "Vendas",
        "Comissão",
        "Líquido",
        "Prêmios",
        "LP",
      ];
    },
    getHistoryAcertoExportHeaders() {
      return [
        "LP período",
        "Saldo ant.",
        "Total devido",
        "Pago",
        "Em aberto",
        "Status",
      ];
    },
    mapHistoryAcertoRow(item, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      return [
        money(item.liquidPrizePeriod),
        money(item.previousOpen),
        money(item.totalDue),
        money(item.amountPaid),
        money(item.amountOpen),
        this.statusLabel(item.status),
      ];
    },
    printHistoryReport(items = null) {
      const settlements =
        Array.isArray(items) && items.length ? items : this.settlements;
      if (!settlements.length) {
        toast.warning("Não há dados para imprimir");
        return;
      }
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Não foi possível abrir a janela de impressão");
        return;
      }
      printWindow.document.write(this.generateHistoryPrintHTML(settlements));
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },
    generateHistoryPrintHTML(items = null) {
      const settlements =
        Array.isArray(items) && items.length ? items : this.settlements;
      const sellerHeaders = this.getHistorySellerExportHeaders()
        .map((h) => `<th>${h}</th>`)
        .join("");
      const acertoHeaders = this.getHistoryAcertoExportHeaders()
        .map((h) => `<th>${h}</th>`)
        .join("");

      const sections = settlements
        .map((item) => {
          const sellers = item.sellers || [];
          const sellerRows = sellers
            .map(
              (seller) =>
                `<tr>${this.mapHistorySellerRow(seller, true)
                  .map((cell) => `<td>${cell}</td>`)
                  .join("")}</tr>`
            )
            .join("");

          const acertoCells = this.mapHistoryAcertoRow(item, true)
            .map((cell) => `<td>${cell}</td>`)
            .join("");

          return `
            <section class="settlement-block">
              <h2>${item.collectorName || "Cobrador"}${
                item.regionName ? ` — ${item.regionName}` : ""
              }</h2>
              <p class="settlement-meta">
                Período: ${this.formatDateTime(item.periodStart)} — ${this.formatDateTime(
                  item.periodEnd
                )} | Criado: ${this.formatDateTime(item.createdAt)}
              </p>

              <table>
                <thead><tr>${sellerHeaders}</tr></thead>
                <tbody>
                  ${
                    sellerRows ||
                    `<tr><td colspan="7">Nenhum vendedor neste fechamento</td></tr>`
                  }
                </tbody>
                ${
                  sellers.length
                    ? `<tfoot><tr>${this.mapHistorySellerTotalRow(item, true)
                        .map((cell) => `<td><strong>${cell}</strong></td>`)
                        .join("")}</tr></tfoot>`
                    : ""
                }
              </table>

              <table class="acerto-table">
                <thead><tr>${acertoHeaders}</tr></thead>
                <tbody><tr class="acerto-row">${acertoCells}</tr></tbody>
              </table>
            </section>
          `;
        })
        .join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Histórico de Fechamentos</title>
          <style>
            @media print {
              @page { size: landscape; margin: 1cm; }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            h1 { text-align: left; color: #2c3e50; font-size: 18px; margin-bottom: 6px; }
            h2 { margin: 22px 0 4px; color: #334155; font-size: 13px; }
            .report-info { margin-bottom: 14px; color: #666; font-size: 12px; }
            .settlement-meta { margin: 0 0 8px; color: #64748b; font-size: 11px; }
            .settlement-block { page-break-inside: avoid; margin-bottom: 22px; }
            table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 10px; }
            th { background: #e5e5e5; padding: 6px; border: 1px solid #ddd; }
            td { padding: 6px; border: 1px solid #ddd; text-align: center; }
            tr:nth-child(even) { background: #f9f9f9; }
            tfoot td { background: #e5e5e5; font-weight: bold; }
            .acerto-table { margin-top: 8px; }
            .acerto-table th { background: #dbeafe; }
            .acerto-row td { background: #eff6ff; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Histórico de Fechamentos</h1>
          <div class="report-info">
            <p>Status: ${this.historyStatus || "Todos"}</p>
          </div>
          ${sections}
        </body>
        </html>
      `;
    },
    exportHistoryToPDF(items = null) {
      const settlements =
        Array.isArray(items) && items.length ? items : this.settlements;
      if (!settlements.length) {
        toast.warning("Não há dados para exportar");
        return;
      }
      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        doc.setFontSize(16);
        doc.text("Histórico de Fechamentos", 14, 15);
        doc.setFontSize(10);
        doc.text(`Status: ${this.historyStatus || "Todos"}`, 14, 22);

        const sellerHeaders = this.getHistorySellerExportHeaders();
        const acertoHeaders = this.getHistoryAcertoExportHeaders();
        let startY = 28;

        settlements.forEach((item, index) => {
          if (index > 0) {
            startY = (doc.lastAutoTable?.finalY || startY) + 12;
            if (startY > 180) {
              doc.addPage();
              startY = 16;
            }
          }

          doc.setFontSize(11);
          doc.text(
            `${item.collectorName || "Cobrador"}${
              item.regionName ? ` — ${item.regionName}` : ""
            }`,
            14,
            startY
          );
          doc.setFontSize(9);
          doc.text(
            `Período: ${this.formatDateTime(item.periodStart)} — ${this.formatDateTime(
              item.periodEnd
            )} | Criado: ${this.formatDateTime(item.createdAt)}`,
            14,
            startY + 5
          );

          const sellers = item.sellers || [];
          autoTable(doc, {
            head: [sellerHeaders],
            body:
              sellers.length > 0
                ? sellers.map((s) => this.mapHistorySellerRow(s, true))
                : [["Nenhum vendedor neste fechamento", "", "", "", "", "", ""]],
            foot: sellers.length
              ? [this.mapHistorySellerTotalRow(item, true)]
              : undefined,
            startY: startY + 8,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
            footStyles: {
              fillColor: [229, 229, 229],
              textColor: [0, 0, 0],
              fontStyle: "bold",
            },
            alternateRowStyles: { fillColor: [249, 249, 249] },
            margin: { left: 14, right: 14 },
          });

          autoTable(doc, {
            head: [acertoHeaders],
            body: [this.mapHistoryAcertoRow(item, true)],
            startY: (doc.lastAutoTable?.finalY || startY) + 4,
            styles: { fontSize: 7, cellPadding: 2, fontStyle: "bold" },
            headStyles: { fillColor: [219, 234, 254], textColor: [0, 0, 0] },
            bodyStyles: { fillColor: [239, 246, 255] },
            margin: { left: 14, right: 14 },
          });
        });

        const suffix =
          settlements.length === 1
            ? settlements[0].collectorName || "Cobrador"
            : "Todos";
        doc.save(`Historico_Fechamentos_Cobrador_${suffix}.pdf`);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error(error);
        toast.error("Erro ao exportar PDF");
      }
    },
    exportHistoryToExcel() {
      if (!this.settlements.length) {
        toast.warning("Não há dados para exportar");
        return;
      }
      try {
        const sellerHeaders = this.getHistorySellerExportHeaders();
        const acertoHeaders = this.getHistoryAcertoExportHeaders();
        const allData = [
          ["Histórico de Fechamentos"],
          [""],
          ["Status:", this.historyStatus || "Todos"],
          [
            "Data de geração:",
            jsFunctions.setDateTimeDisplay(new Date().toISOString()),
          ],
          [""],
        ];

        this.settlements.forEach((item) => {
          allData.push([
            `${item.collectorName || "Cobrador"}${
              item.regionName ? ` — ${item.regionName}` : ""
            }`,
          ]);
          allData.push([
            "Período:",
            `${this.formatDateTime(item.periodStart)} — ${this.formatDateTime(
              item.periodEnd
            )}`,
            "Criado em:",
            this.formatDateTime(item.createdAt),
          ]);
          allData.push([""]);
          allData.push(["Vendedores"]);
          allData.push(sellerHeaders);
          const sellers = item.sellers || [];
          if (sellers.length) {
            sellers.forEach((seller) => {
              allData.push(this.mapHistorySellerRow(seller, false));
            });
            allData.push(this.mapHistorySellerTotalRow(item, false));
          } else {
            allData.push(["Nenhum vendedor neste fechamento"]);
          }
          allData.push([""]);
          allData.push(["Acerto"]);
          allData.push(acertoHeaders);
          allData.push(this.mapHistoryAcertoRow(item, false));
          allData.push([""]);
          allData.push([""]);
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(allData);
        ws["!cols"] = sellerHeaders.map(() => ({ wch: 16 }));
        XLSX.utils.book_append_sheet(wb, ws, "Fechamentos");
        XLSX.writeFile(wb, "Historico_Fechamentos_Cobrador.xlsx");
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error(error);
        toast.error("Erro ao exportar Excel");
      }
    },
    async submitPayment() {
      const s = this.paymentModal.settlement;
      if (!s) return;
      const amount = Number(this.paymentModal.amount);
      if (!amount || amount <= 0) {
        toast.error("Informe um valor válido.");
        return;
      }
      if (amount > Number(s.amountOpen)) {
        toast.error("Valor não pode exceder o saldo em aberto.");
        return;
      }

      const doSubmit = async () => {
        this.savingPayment = true;
        try {
          await mainService.addCollectorSettlementPayment(s.id, {
            amount,
            paymentDate: this.toApiDateStart(this.paymentModal.date),
            notes: this.paymentModal.notes || null,
            registeredByUserId: this.currentUser?.id || null,
            resetSellerWallets: !!this.paymentModal.resetSellerWallets,
          });
          toast.success(
            this.paymentModal.resetSellerWallets
              ? "Recebimento registrado e saldos das carteiras resetados!"
              : "Recebimento registrado!"
          );
          this.closePaymentModal();
          await this.loadSettlements();
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.savingPayment = false;
        }
      };

      await doSubmit();
    },
    deleteSettlement(item) {
      if (!this.canDeleteSettlement(item)) {
        toast.error("Não é possível excluir um fechamento que já possui pagamento.");
        return;
      }

      this.$confirm({
        message: `Confirma a exclusão do fechamento de ${
          item.collectorName || "cobrador"
        } (${this.formatDateTime(item.periodStart)} — ${this.formatDateTime(
          item.periodEnd
        )})?`,
        button: { no: "Não", yes: "Sim" },
        callback: async (confirm) => {
          if (!confirm) return;
          this.deletingSettlementId = item.id;
          try {
            await mainService.deleteCollectorSettlement(item.id);
            toast.success("Fechamento excluído com sucesso!");
            if (this.detailModal?.settlement?.id === item.id) {
              this.closeDetail();
            }
            if (this.receiptModal?.settlement?.id === item.id) {
              this.closeReceipt();
            }
            await this.loadSettlements();
            await this.refreshLastSettlementsForSelection();
          } catch (error) {
            toast.error(handleErrorMessage(error));
          } finally {
            this.deletingSettlementId = null;
          }
        },
      });
    },
  },
};
</script>

<style scoped>
.entries-collectors-container {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
  color: var(--text-primary);
}

.entries-collectors-container .filters-card {
  overflow: visible !important;
}

.filters-card,
.results-card,
.summary-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 1.25rem;
  overflow: visible;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--secondary-color), #f1f1f1);
  flex-wrap: wrap;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

.results-card {
  overflow: visible !important;
}

.results-toolbar-card .card-header {
  border-bottom: none;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

.card-actions .btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.card-actions .btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

[data-theme="dark"] .card-header {
  background: linear-gradient(135deg, var(--secondary-color), #17171a);
}

.card-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-title i {
  color: var(--accent-color);
}

.muted {
  color: var(--text-muted, var(--text-secondary));
  font-weight: 500;
  font-size: 0.9rem;
}

.filters-content {
  padding: 1.25rem;
  overflow: visible !important;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
  overflow: visible !important;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  overflow: visible !important;
  position: relative !important;
  z-index: 1 !important;
}

.input-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-input {
  border: 2px solid var(--border-color);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.95rem;
  background: var(--input-background, var(--card-background));
  color: var(--text-primary);
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 0.2rem var(--shadow);
}

.multiselect-container {
  width: 100%;
  position: relative;
  z-index: 20;
}

.filter-hint {
  display: block;
  margin-top: 6px;
  color: var(--text-secondary, #6b7280);
  font-size: 0.8rem;
}

.period-auto-info {
  grid-column: 1 / -1;
}

.auto-period-box {
  border: 2px solid var(--border-color);
  border-radius: 10px;
  padding: 12px 14px;
  background: var(--secondary-color, var(--hover-color));
  color: var(--text-primary);
  font-size: 0.92rem;
  line-height: 1.4;
}

.auto-period-box strong {
  display: inline-block;
  margin-left: 4px;
  color: var(--accent-color);
}

.filters-actions {
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

/* Mantém sucesso local; btn-primary/btn-secondary usam o padrão global (themes.css) */
.btn-success {
  border: none;
  border-radius: 8px;
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  transition: all 0.3s ease;
  background: #2e7d32;
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-footer .btn-secondary {
  background: transparent;
  color: var(--text-secondary);
  border: 2px solid var(--border-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-footer .btn-secondary:hover:not(:disabled) {
  background: var(--secondary-color);
  color: var(--text-primary);
  border-color: var(--text-secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  padding: 14px 16px;
  margin-bottom: 0;
}

.summary-card.highlight {
  border-color: var(--accent-color, #0288d1);
  background: color-mix(in srgb, var(--accent-color, #0288d1) 12%, var(--card-background));
}

.summary-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text-primary);
}

.summary-value.positive,
.currency-positive {
  color: #2e7d32;
}

.clickable-value {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: all 0.2s ease;
}

.clickable-value:hover {
  opacity: 0.85;
  text-decoration-style: solid;
}

.clickable-value:active {
  transform: scale(0.98);
}

.summary-value.negative,
.currency-negative {
  color: #c62828;
}

.summary-value.warning {
  color: #ef6c00;
}

.mini-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 0.9rem;
  background: var(--bg-secondary, transparent);
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
  padding: 0 0 0.5rem;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: nowrap;
  vertical-align: middle;
}

.modern-table td.name-column {
  text-align: left !important;
}

.modern-table tfoot td {
  background: var(--secondary-color, var(--card-background));
  font-weight: 600;
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

.empty {
  text-align: center !important;
  color: var(--text-secondary);
  padding: 24px !important;
  white-space: normal !important;
}

.badge {
  display: inline-block;
  border-radius: 8px;
  padding: 3px 8px;
  font-size: 0.75rem;
  font-weight: 700;
}

.badge-ok {
  background: rgba(46, 125, 50, 0.15);
  color: #2e7d32;
}

.badge-warn {
  background: rgba(239, 108, 0, 0.15);
  color: #ef6c00;
}

.badge-off {
  background: rgba(198, 40, 40, 0.15);
  color: #c62828;
}

.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.btn-row-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.btn-row-action.btn-edit {
  background: var(--info-color, #3b82f6);
}

.btn-row-action.btn-edit:hover {
  background: var(--info-hover, #2563eb);
  transform: translateY(-2px);
}

.btn-row-action.btn-pay {
  background: #2e7d32;
}

.btn-row-action.btn-pay:hover {
  background: #1b5e20;
  transform: translateY(-2px);
}

.btn-row-action.btn-receipt {
  background: #c2410c;
}

.btn-row-action.btn-receipt:hover {
  background: #9a3412;
  transform: translateY(-2px);
}

.btn-row-action.btn-delete {
  background: #dc2626;
}

.btn-row-action.btn-delete:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-2px);
}

.btn-row-action.btn-delete:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 768px) {
  .card-actions {
    width: 100%;
  }

  .card-actions .btn-action {
    flex: 1 1 auto;
    justify-content: center;
  }

  .columns-dropdown {
    flex: 1 1 auto;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.modal-content {
  background: var(--card-background);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-lg, 0 20px 40px rgba(0, 0, 0, 0.3));
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.detail-modal {
  max-width: 760px;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  background: var(--card-background);
}

.modal-title {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.modal-title i {
  color: var(--accent-color);
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.modal-close:hover {
  background: var(--hover-color);
  color: var(--text-primary);
  transform: scale(1.1);
}

.modal-body {
  padding: 1.5rem;
  color: var(--text-primary);
  background: var(--card-background);
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  background: var(--card-background);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.input-label.required::after {
  content: " *";
  color: var(--danger-color, #dc3545);
}

.payment-open-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--secondary-color, var(--hover-color));
  border: 2px solid var(--accent-color);
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.25rem;
}

.wallet-reset-overlay {
  z-index: 1100;
}

.wallet-reset-modal {
  max-width: 720px;
  width: 100%;
}

.wallet-reset-intro {
  margin: 0 0 1rem;
  color: var(--text-primary);
  line-height: 1.45;
}

.wallet-reset-loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 0;
  color: var(--text-secondary);
}

.wallet-reset-table-wrap {
  max-height: 360px;
  overflow: auto;
}

.wallet-reset-table th,
.wallet-reset-table td {
  white-space: nowrap;
}

.total-label {
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 600;
}

.total-value {
  font-size: 1.25rem;
  font-weight: 700;
  font-family: "Consolas", "Monaco", monospace;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.section-title {
  margin: 12px 0 8px;
  font-size: 0.95rem;
  color: var(--text-primary);
}

.modal-content.receipt-modal {
  max-width: 420px;
}

.receipt-modal-body {
  background: #e5e5e5;
  display: flex;
  justify-content: center;
}

.receipt-ticket {
  width: 300px;
  margin: 0 auto;
  padding: 14px 12px 18px;
  background: #fff;
  color: #111;
  font-family: "Courier New", Courier, monospace;
  font-size: 13px;
  line-height: 1.35;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.receipt-logo {
  display: block;
  max-width: 180px;
  max-height: 64px;
  margin: 0 auto 8px;
  object-fit: contain;
}

.receipt-dash {
  border-top: 1px dashed #111;
  margin: 8px 0;
}

.receipt-dash-short {
  border-top: 1px dashed #111;
  margin: 4px 0 4px 40%;
}

.receipt-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: flex-start;
}

.receipt-row span:last-child {
  text-align: right;
  word-break: break-word;
}

.receipt-center {
  text-align: center;
}

.receipt-title {
  font-weight: 700;
  letter-spacing: 1px;
  margin-top: 4px;
}

.receipt-subtitle {
  font-size: 11px;
  margin-bottom: 4px;
}

.receipt-strong {
  font-weight: 700;
}

.receipt-foot {
  font-size: 11px;
  margin-top: 8px;
}

.muted-receipt {
  font-size: 12px;
  color: #555;
}

.receipt-seller {
  margin: 2px 0;
}

.receipt-actions {
  flex-wrap: wrap;
}

.receipt-actions .btn-success {
  background: #16a34a;
  color: #fff;
}

.modal-content.pdf-modal {
  max-width: 460px;
}

.pdf-modal-hint {
  margin: 0 0 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.pdf-scope-options {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.pdf-scope-option {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-weight: 600;
}
</style>
